import { Drawer, Modal } from "antd";
import { Formik } from "formik";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  PiArrowLeft,
  PiCheck,
  PiCheckCircle,
  PiCopy,
  PiInfo,
  PiMagnifyingGlass,
  PiPencilSimple,
  PiPlus,
  PiShieldCheck,
  PiTag,
  PiTrash,
  PiTruck,
  PiTruckFill,
  PiWarningCircle,
  PiX,
  PiXBold,
} from "react-icons/pi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomTabs from "../../components/CustomTabs";
import Input from "../../components/input";
import Select from "../../components/Select";
import { apiService } from "../../services/apiService";
import {
  fetchBanksDropdown,
  fetchCustomerDropdown,
} from "../../store/Slices/dropdownSlice";
import { fetchVehicleTypesDropdown } from "../../store/Slices/VehicleDropdownSlice";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/DriverSafety.css";
import "../../styles/Input.css";
import "../../styles/Issue.css";
import "../../styles/Trip.css";
import "../../styles/TripCostEstimation.css";
import { AppMainModules, ContractPermission } from "../../utils/app-constant";
import { paymentTermsOptions } from "../../utils/Dropdowns";
import { formats, hasPermission, modules } from "../../utils/helper";
import SpinnerLoader from "../../utils/SpinnerLoader";
import {
  activeDetailsIcon,
  detailsIcon,
  dollarActiveIcon,
  dollarIcon,
} from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import { createContractSchema } from "./createContract.schema";
import {
  componentTypeOptions,
  fixedTypeOptions,
  getFixedTypeLabel,
  getTripDataTypeLabel,
  getVariableTypeLabel,
  storageTripDataTypeOptions,
  tripDataTypeOptions,
} from "./createContract.enums";

const parseNumberOrZero = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? 0 : numericValue;
};

const normalizeFormulaVariableValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const createFormulaToken = (type, value, label = "", metadata = {}) => ({
  id: `formula-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type,
  value,
  label,
  ...metadata,
});

const isFormulaOperand = (token = {}) =>
  ["tripData", "variableRate", "fixedCharge", "hardcodedValue"].includes(
    String(token?.type || "")
  );

const validateFormulaTokens = (tokens = []) => {
  const normalizedTokens = Array.isArray(tokens) ? tokens.filter(Boolean) : [];

  if (!normalizedTokens.length) {
    return "Formula is required";
  }

  let bracketBalance = 0;
  let previousKind = "start";

  for (let index = 0; index < normalizedTokens.length; index += 1) {
    const token = normalizedTokens[index];
    const tokenValue = String(token?.value || "");

    if (isFormulaOperand(token)) {
      if (previousKind === "operand" || previousKind === "close") {
        return "Add an operator between formula values";
      }
      previousKind = "operand";
      continue;
    }

    if (String(token?.type || "") === "operator" && tokenValue === "(") {
      if (previousKind === "operand" || previousKind === "close") {
        return "Add an operator before opening bracket";
      }
      bracketBalance += 1;
      previousKind = "open";
      continue;
    }

    if (String(token?.type || "") === "operator" && tokenValue === ")") {
      if (bracketBalance <= 0) {
        return "Closing bracket is misplaced";
      }
      if (["start", "operator", "open"].includes(previousKind)) {
        return "Closing bracket cannot appear here";
      }
      bracketBalance -= 1;
      previousKind = "close";
      continue;
    }

    if (String(token?.type || "") === "operator") {
      if (["start", "operator", "open"].includes(previousKind)) {
        return `Operator ${tokenValue} cannot be used here`;
      }
      previousKind = "operator";
      continue;
    }
  }

  if (bracketBalance !== 0) {
    return "Brackets are not balanced";
  }

  if (["operator", "open"].includes(previousKind)) {
    return "Formula cannot end with an operator";
  }

  return "";
};

const buildFormulaPayloadTokens = (
  charge = {},
  { tokensKey = "formulaTokens" } = {}
) => {
  const sourceTokens = Array.isArray(charge?.[tokensKey])
    ? charge[tokensKey]
    : [];

  return sourceTokens
    .map((token, index) => {
      const tokenType = String(token?.type || "").trim();

      if (tokenType === "operator") {
        const operatorValue = String(token?.value || "").trim();
        return operatorValue
          ? {
            position: index + 1,
            type: "operator",
            value: operatorValue,
            label: String(token?.label || operatorValue).trim(),
          }
          : null;
      }

      if (tokenType === "tripData" || tokenType === "trip_data") {
        const tokenLabel = String(
          token?.label || getTripDataTypeLabel(token?.value) || token?.value || ""
        ).trim();
        const tokenValue = normalizeFormulaVariableValue(
          token?.value || tokenLabel
        );
        return tokenValue
          ? {
            position: index + 1,
            type: "trip_data",
            value: tokenValue,
            label: tokenLabel || tokenValue,
          }
          : null;
      }

      if (tokenType === "variableRate") {
        const tokenValue = String(
          token?.value || charge?.variableRate || ""
        ).trim();
        return tokenValue
          ? {
            position: index + 1,
            type: "constant",
            value: tokenValue,
            label: String(
              token?.label ||
              getVariableTypeLabel(charge?.variableType) ||
              "Variable"
            ).trim(),
          }
          : null;
      }

      if (tokenType === "fixedCharge" || tokenType === "fixed_charge") {
        return {
          position: index + 1,
          type: "fixed_charge",
          value: String(token?.value || "").trim(),
          label: String(token?.label || "").trim(),
        };
      }

      if (tokenType === "hardcodedValue" || tokenType === "constant") {
        return {
          position: index + 1,
          type: "constant",
          value: String(token?.value || "").trim(),
          label: String(token?.label || token?.value || "Value").trim(),
        };
      }

      const fallbackLabel = String(token?.label || token?.value || "").trim();
      const fallbackValue = normalizeFormulaVariableValue(
        token?.value || fallbackLabel
      );

      return fallbackValue
        ? {
          position: index + 1,
          type: "constant",
          value: fallbackValue,
          label: fallbackLabel || fallbackValue,
        }
        : null;
    })
    .filter(Boolean);
};

const createDefaultDrawerCharge = (vehicleTypeOption = {}) => ({
  id: `charge-${Date.now()}`,
  vehicleTypeId: vehicleTypeOption?.value || "",
  vehicleType: vehicleTypeOption?.label || String(vehicleTypeOption || ""),
  numberOfVehicle: "1",
  monthlyCharges: "0.00",
  variableType: "",
  variableRate: "",
  fixedCharges: [],
  formulaTripData: "",
  formulaTokens: [],
  formulaRules: [],
  formulaHardcodedValue: "",
  shortagesFormulaTripData: "",
  shortagesFormulaTokens: [],
  shortagesFormulaRules: [],
  shortagesFormulaHardcodedValue: "",
  chargeShortage: false,
  isExisting: false,
});

const renderFormulaBadges = (tokens) => {
  const tokenList = safeArray(tokens);
  if (!tokenList.length) {
    return (
      <span style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic" }}>
        Formula not configured
      </span>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        alignItems: "center",
        maxWidth: "100%",
        padding: "1px 0",
      }}
    >
      {tokenList.map((t, idx) => {
        const type = t.type;
        const isOp = type === "operator";
        const isConst = type === "constant" || type === "hardcodedValue";
        let label = t.label || t.value;
        if (isOp) {
          if (label === "*") label = "×";
          if (label === "/") label = "÷";
        }

        return (
          <span
            key={idx}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: isOp ? "1px 6px" : "2px 8px",
              borderRadius: "4px",
              fontSize: "11px",
              fontWeight: 600,
              backgroundColor: isOp ? "#f1f5f9" : isConst ? "#ecfdf5" : "#eff6ff",
              color: isOp ? "#334155" : isConst ? "#047857" : "#1d4ed8",
              border: `1px solid ${isOp ? "#cbd5e1" : isConst ? "#a7f3d0" : "#bfdbfe"}`,
            }}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
};

const statusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const billingFrequencyOptions = [
  { label: "Weekly", value: "weekly" },
  { label: "Fortnightly ", value: "fortnight" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const paymentMethodOptions = [
  { label: "Bank Transfer", value: "bank_transfer" },
  { label: "Cash", value: "cash" },
];

const emptyPricingTemplateValues = {
  systemId: "",
  templateName: "",
  customer: "",
  status: "",
  vehicleTypes: [],
  effectiveFrom: "",
  effectiveTo: "",
  description: "",
  attachments: [],
  billingFrequency: "",
  paymentMethod: "",
  paymentTerms: "",
  bank: "",
  accountTitle: "",
  iban: "",
  notes: "",
  charges: [],
};

const toText = (value) => String(value ?? "").trim();

const safeArray = (value) => (Array.isArray(value) ? value : []);

const formatDateInput = (value) => {
  const normalizedValue = toText(value);
  if (!normalizedValue) return "";
  const directDateMatch = normalizedValue.match(/^\d{4}-\d{2}-\d{2}/);
  if (directDateMatch) return directDateMatch[0];

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) return "";
  return parsedDate.toISOString().slice(0, 10);
};

const formatContractDateTime = (value, useEndOfDay = false) => {
  const normalizedValue = toText(value);
  if (!normalizedValue) return "";
  return `${normalizedValue} ${useEndOfDay ? "23:59:59" : "00:00:00"}`;
};



const resolveDropdownCollection = (source, candidateKeys = []) => {
  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  const normalizedCandidateKeys = candidateKeys.map((key) =>
    String(key || "").toLowerCase(),
  );

  for (const [key, value] of Object.entries(source)) {
    if (!Array.isArray(value)) continue;

    const normalizedKey = String(key || "").toLowerCase();
    if (
      normalizedCandidateKeys.includes(normalizedKey) ||
      normalizedCandidateKeys.some((candidate) =>
        normalizedKey.includes(candidate),
      )
    ) {
      return value;
    }
  }

  return [];
};

const normalizeSelectOptions = (
  source,
  { valueKeys = ["value", "id"], labelKeys = ["label", "name", "title"] } = {},
) =>
  safeArray(source)
    .map((item, index) => {
      if (item === null || item === undefined) return null;

      if (typeof item === "string" || typeof item === "number") {
        return {
          value: item,
          label: String(item),
        };
      }

      if (typeof item !== "object") return null;

      const resolvedValue = valueKeys.reduce(
        (result, key) => (result !== undefined ? result : item?.[key]),
        undefined,
      );
      const resolvedLabel = labelKeys.reduce(
        (result, key) => (result !== undefined ? result : item?.[key]),
        undefined,
      );

      if (resolvedValue === undefined && resolvedLabel === undefined)
        return null;

      return {
        ...item,
        value: resolvedValue ?? resolvedLabel ?? index + 1,
        label: String(resolvedLabel ?? resolvedValue ?? `Option ${index + 1}`),
      };
    })
    .filter(
      (option) =>
        option &&
        option.value !== undefined &&
        option.value !== null &&
        toText(option.label),
    );

const prependMissingOption = (options = [], nextOption = null) => {
  if (!nextOption?.value && !nextOption?.label) return options;

  const optionValue = nextOption?.value ?? nextOption?.label;
  if (
    safeArray(options).some(
      (option) => String(option?.value ?? "") === String(optionValue ?? ""),
    )
  ) {
    return options;
  }

  return [
    {
      value: optionValue,
      label: nextOption?.label || String(optionValue || ""),
    },
    ...options,
  ];
};

const pickOption = (options = [], value = "") =>
  safeArray(options).find(
    (option) => String(option?.value ?? "") === String(value ?? ""),
  ) || null;

const pickMultiOptions = (options = [], values = []) =>
  safeArray(options).filter((option) =>
    safeArray(values).some(
      (value) => String(value ?? "") === String(option?.value ?? ""),
    ),
  );

const parseIdOrRaw = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

const resolveUploadedFilePath = (fileEntry) => {
  if (typeof fileEntry === "string") return toText(fileEntry);
  if (!fileEntry || typeof fileEntry !== "object") return "";

  return toText(
    fileEntry?.fileName ||
    fileEntry?.file_path ||
    fileEntry?.file_name ||
    fileEntry?.path ||
    fileEntry?.url ||
    fileEntry?.file ||
    fileEntry?.image ||
    (typeof fileEntry?.data === "string" ? fileEntry.data : "") ||
    fileEntry?.data?.fileName ||
    fileEntry?.data?.file_path ||
    fileEntry?.data?.file_name ||
    fileEntry?.data?.path ||
    fileEntry?.data?.url ||
    fileEntry?.data?.file ||
    fileEntry?.data?.image ||
    "",
  );
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const splitDescriptionAndNotes = (detail = {}) => {
  const apiDescription = toText(detail?.description);
  const parts = apiDescription
    .split("|")
    .map((part) => toText(part))
    .filter(Boolean);

  return {
    description: toText(
      detail?.template_description ||
      detail?.detail ||
      parts[0] ||
      apiDescription,
    ),
    notes: toText(detail?.note || detail?.notes || parts.slice(1).join(" | ")),
  };
};

const normalizePricingTemplateDetail = (detail = {}, fallback = {}) => {
  const descriptionMeta = splitDescriptionAndNotes(detail);
  const vehicleTypeRows = safeArray(
    detail?.contracts_vehicle_types ?? detail?.contract_template_vehicle_types,
  );
  const vehicleTypes = vehicleTypeRows
    .map((item) => item?.vehicle_type_id ?? item?.vehicle_type?.id ?? item?.id)
    .filter((value) => value !== null && value !== undefined && value !== "");

  return {
    ...emptyPricingTemplateValues,
    id: detail?.id ?? fallback?.id ?? "",
    systemId:
      toText(detail?.contracts_template_id) ||
      toText(fallback?.templateId) ||
      (detail?.id ? `CTT-${detail.id}` : ""),
    templateName:
      toText(detail?.name) ||
      toText(fallback?.name) ||
      toText(detail?.contracts_template_id),
    customer:
      detail?.customer_id ??
      detail?.customer?.id ??
      fallback?.customerId ??
      fallback?.customer ??
      "",
    customerLabel: toText(
      detail?.customer?.name ||
      detail?.customer?.customer_name ||
      fallback?.customer,
    ),
    status: toText(detail?.status || fallback?.statusValue).toLowerCase(),
    vehicleTypes,
    vehicleTypeLabels: vehicleTypeRows.map((item) => ({
      value: item?.vehicle_type_id ?? item?.vehicle_type?.id ?? item?.id ?? "",
      label:
        toText(
          item?.vehicle_type?.title ||
          item?.vehicle_type?.name ||
          item?.vehicle_type_name ||
          item?.vehicle_type,
        ) || `Vehicle Type ${item?.vehicle_type_id ?? item?.id ?? ""}`,
    })),
    effectiveFrom: formatDateInput(detail?.start_at || fallback?.effectiveFrom),
    effectiveTo: formatDateInput(detail?.expire_at || fallback?.effectiveTo),
    description: descriptionMeta.description,
    attachments: safeArray(detail?.files || detail?.attachments),
    billingFrequency: toText(detail?.billing_frequency),
    paymentMethod: toText(detail?.payment_method),
    paymentTerms: toText(detail?.payment_term || detail?.payment_terms),
    bank: detail?.bank_id ?? detail?.bank_name ?? "",
    bankLabel: toText(detail?.bank?.name || detail?.bank_name),
    accountTitle: toText(detail?.account_title),
    iban: toText(detail?.iban_number || detail?.iban),
    notes: descriptionMeta.notes,
    charges: [],
  };
};

const buildContractDescription = (values = {}) =>
  [toText(values?.description), toText(values?.notes)]
    .filter(Boolean)
    .join(" | ");

const buildUpdatePayload = (values = {}, rawDetail = {}) => ({
  name: toText(values.templateName),
  customer_id: parseIdOrRaw(values.customer),
  start_at: formatContractDateTime(values.effectiveFrom),
  expire_at: formatContractDateTime(values.effectiveTo, true),
  status: toText(values.status).toLowerCase() || "pending",
  description: buildContractDescription(values),
  billing_frequency: toText(values.billingFrequency),
  payment_method: toText(values.paymentMethod),
  payment_term: toText(values.paymentTerms),
  bank_name: toText(values.bank),
  account_title: toText(values.accountTitle),
  iban_number: toText(values.iban),
  files: normalizeToArray(values.attachments)
    .map((file) => resolveUploadedFilePath(file))
    .filter(Boolean),
  contract_template_vehicle_types: safeArray(
    rawDetail?.contracts_vehicle_types ??
    rawDetail?.contract_template_vehicle_types,
  ),
});

const ContractDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("general");
  const [pageLoading, setPageLoading] = useState(true);
  const [templateRecord, setTemplateRecord] = useState(null);
  const [rawDetail, setRawDetail] = useState(null);
  const locationTemplateRecord = useMemo(
    () => location.state?.template || {},
    [location.state],
  );
  const canUpdateTemplate = hasPermission({
    permissionSlug: ContractPermission.Update,
    menuSlug: "contract",
    moduleSlug: AppMainModules.contract,
  });

  const {
    customerDropdownData = [],
    banksDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns || {});
  const { vehicleTypesDropdownData = [], vehicleDropdownLoadingCount = 0 } =
    useSelector((state) => state.vehicleDropdowns || {});

  const customerOptions = useMemo(
    () =>
      normalizeSelectOptions(
        resolveDropdownCollection(customerDropdownData, ["customer"]),
        {
          valueKeys: ["value", "id", "customer_id"],
          labelKeys: ["label", "name", "customer_name", "title"],
        },
      ),
    [customerDropdownData],
  );

  const vehicleTypeDropdownOptions = useMemo(
    () =>
      normalizeSelectOptions(
        resolveDropdownCollection(vehicleTypesDropdownData, [
          "vehicletype",
          "types",
        ]),
        {
          valueKeys: ["value", "id", "vehicle_type_id"],
          labelKeys: ["label", "name", "title", "vehicle_type_name"],
        },
      ),
    [vehicleTypesDropdownData],
  );

  useEffect(() => {
    dispatch(fetchCustomerDropdown());
    dispatch(fetchBanksDropdown());
    dispatch(fetchVehicleTypesDropdown());
  }, [dispatch]);

  const [isAddVehicleDrawerOpen, setIsAddVehicleDrawerOpen] = useState(false);
  const [addingVehicleType, setAddingVehicleType] = useState(false);
  const [updatingVehicleSetting, setUpdatingVehicleSetting] = useState(false);
  const [drawerCharge, setDrawerCharge] = useState(createDefaultDrawerCharge());
  const [selectedAddVehicleType, setSelectedAddVehicleType] = useState("");

  const [activeCategory, setActiveCategory] = useState(null);
  const [activeShortagesCategory, setActiveShortagesCategory] = useState(null);
  const [tempChargeType, setTempChargeType] = useState("");
  const [tempChargeValue, setTempChargeValue] = useState("");

  const [drawerErrors, setDrawerErrors] = useState({});
  const [drawerTouched, setDrawerTouched] = useState(false);

  // Contract Vehicles Tab State
  const [vehicleSearchQuery, setVehicleSearchQuery] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignVehicleTypeId, setAssignVehicleTypeId] = useState("");
  const [assignSelectedVehicleIds, setAssignSelectedVehicleIds] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [loadingAvailableVehicles, setLoadingAvailableVehicles] = useState(false);
  const [savingAssignedVehicles, setSavingAssignedVehicles] = useState(false);
  const [copiedRegNo, setCopiedRegNo] = useState(null);
  const [assignSearchQuery, setAssignSearchQuery] = useState("");

  const unselectedVehicleTypeOptions = useMemo(() => {
    const existingTypeIds = safeArray(
      rawDetail?.contracts_vehicle_types || rawDetail?.contract_template_vehicle_types
    ).map((cvt) => String(cvt.vehicle_type_id || cvt.vehicle_type?.id));

    return vehicleTypeDropdownOptions.filter(
      (opt) => !existingTypeIds.includes(String(opt.value))
    );
  }, [vehicleTypeDropdownOptions, rawDetail]);

  const resetDrawerForm = () => {
    setDrawerCharge(createDefaultDrawerCharge());
    setActiveCategory(null);
    setActiveShortagesCategory(null);
    setTempChargeType("");
    setTempChargeValue("");
    setSelectedAddVehicleType("");
    setDrawerErrors({});
    setDrawerTouched(false);
  };

  const handleEditVehicleTypeFormula = (cvt) => {
    const vId = cvt.vehicle_type_id || cvt.vehicle_type?.id;
    const vtOption = vehicleTypeDropdownOptions.find(
      (o) => String(o.value) === String(vId)
    ) || {
      value: vId,
      label: cvt.vehicle_type?.title || cvt.vehicle_type?.name || `Vehicle Type ${vId}`,
    };

    const mappedFormulaTokens = safeArray(cvt.formula_tokens).map((t, idx) => {
      let tType = t.type;
      if (tType === "trip_data") tType = "tripData";
      if (tType === "constant") tType = "hardcodedValue";
      if (tType === "fixed_charge") tType = "fixedCharge";

      return {
        id: `token-${idx}-${Date.now()}`,
        type: tType,
        value: t.value,
        label: t.label || t.value,
      };
    });

    const mappedShortagesTokens = safeArray(
      cvt.shortage_formula_tokens || cvt.shortages_formula_tokens
    ).map((t, idx) => {
      let tType = t.type;
      if (tType === "trip_data") tType = "tripData";
      if (tType === "constant") tType = "hardcodedValue";

      return {
        id: `shortage-token-${idx}-${Date.now()}`,
        type: tType,
        value: t.value,
        label: t.label || t.value,
      };
    });

    const mappedFixedCharges = safeArray(
      cvt.charges || cvt.contract_template_vehicle_types_charges
    ).map((fc, idx) => ({
      id: `fixed-${idx}-${Date.now()}`,
      fixedType: fc.fix_charge_type || fc.type,
      fixedCharge: parseNumberOrZero(fc.fix_charge_rate || fc.rate),
    }));

    setDrawerCharge({
      id: `charge-${cvt.contract_template_vehicle_type_id || cvt.id || Date.now()}`,
      vehicleTypeId: vtOption.value,
      vehicleType: vtOption.label,
      numberOfVehicle: String(cvt.no_of_vehicle || "1"),
      monthlyCharges: String(cvt.monthly_rental || "0.00"),
      variableType: cvt.variable_charge_type || "",
      variableRate: cvt.variable_charge_rate ? String(cvt.variable_charge_rate) : "",
      fixedCharges: mappedFixedCharges,
      formulaTokens: mappedFormulaTokens,
      formulaRules: mappedFormulaTokens.length ? [{ tokens: mappedFormulaTokens }] : [],
      formulaHardcodedValue: "",
      shortagesFormulaTokens: mappedShortagesTokens,
      shortagesFormulaRules: mappedShortagesTokens.length ? [{ tokens: mappedShortagesTokens }] : [],
      shortagesFormulaHardcodedValue: "",
      chargeShortage: !!(cvt.charge_shortage || mappedShortagesTokens.length > 0),
      isExisting: true,
    });

    setIsAddVehicleDrawerOpen(true);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (activeCategory && !event.target.closest(".tab-popover-wrapper")) {
        setActiveCategory(null);
      }
      if (activeShortagesCategory && !event.target.closest(".tab-popover-wrapper")) {
        setActiveShortagesCategory(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [activeCategory, activeShortagesCategory]);

  const refetchContractDetail = async () => {
    try {
      const response = await apiService.getContractDetail(id);
      const detail = response?.data || {};
      setRawDetail(detail);
      setTemplateRecord(
        normalizePricingTemplateDetail(detail, locationTemplateRecord),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAvailableVehiclesForType = async (vehicleTypeId) => {
    if (!vehicleTypeId) {
      setAvailableVehicles([]);
      return;
    }
    setLoadingAvailableVehicles(true);
    try {
      const response = await apiService.getVehiclesDropdownByVehicleType(vehicleTypeId);
      const list = response?.data || response || [];
      const normalized = safeArray(list).map((v) => ({
        id: v.id || v.value,
        registration_number: v.registration_number || v.reg_number || v.label || `Vehicle #${v.id || v.value}`,
        registration_number_slug: v.registration_number_slug || v.slug || "",
        vehicle_type: v.vehicle_type || null,
      }));
      setAvailableVehicles(normalized);
    } catch (err) {
      try {
        const fallbackRes = await apiService.getVehiclesDropdown();
        const fList = fallbackRes?.data || fallbackRes || [];
        const normalized = safeArray(fList).map((v) => ({
          id: v.id || v.value,
          registration_number: v.registration_number || v.reg_number || v.label || `Vehicle #${v.id || v.value}`,
          registration_number_slug: v.registration_number_slug || v.slug || "",
          vehicle_type: v.vehicle_type || null,
        }));
        setAvailableVehicles(normalized);
      } catch (fallbackErr) {
        setAvailableVehicles([]);
      }
    } finally {
      setLoadingAvailableVehicles(false);
    }
  };

  const handleOpenAssignModal = (vehicleTypeId = "") => {
    const configuredTypes = safeArray(
      rawDetail?.contracts_vehicle_types || rawDetail?.contract_template_vehicle_types
    );
    const defaultTypeId =
      vehicleTypeId ||
      configuredTypes[0]?.vehicle_type_id ||
      configuredTypes[0]?.vehicle_type?.id ||
      "";
    setAssignVehicleTypeId(String(defaultTypeId));
    setAssignSearchQuery("");

    const allAssigned = safeArray(rawDetail?.contract_vehicles);
    const assignedForType = allAssigned
      .filter((cv) => String(cv.vehicle_type_id) === String(defaultTypeId))
      .map((cv) => cv.vehicle_id || cv.vehicle?.id);

    setAssignSelectedVehicleIds(assignedForType);
    setIsAssignModalOpen(true);
    if (defaultTypeId) {
      fetchAvailableVehiclesForType(defaultTypeId);
    }
  };

  const handleVehicleTypeChangeInAssignModal = (typeId) => {
    setAssignVehicleTypeId(String(typeId));
    setAssignSearchQuery("");
    const allAssigned = safeArray(rawDetail?.contract_vehicles);
    const assignedForType = allAssigned
      .filter((cv) => String(cv.vehicle_type_id) === String(typeId))
      .map((cv) => cv.vehicle_id || cv.vehicle?.id);
    setAssignSelectedVehicleIds(assignedForType);
    fetchAvailableVehiclesForType(typeId);
  };

  const handleToggleVehicleSelection = (vId) => {
    setAssignSelectedVehicleIds((prev) =>
      prev.includes(vId) ? prev.filter((id) => id !== vId) : [...prev, vId]
    );
  };

  const handleSaveAssignedVehicles = async () => {
    if (!assignVehicleTypeId) {
      errorNotify("Please select a vehicle type");
      return;
    }
    setSavingAssignedVehicles(true);
    try {
      await apiService.saveContractVehicles({
        contract_id: parseIdOrRaw(id),
        vehicle_type_id: parseIdOrRaw(assignVehicleTypeId),
        vehicles_ids: assignSelectedVehicleIds,
      });
      successNotify("Contract vehicles assigned successfully");
      setIsAssignModalOpen(false);
      await refetchContractDetail();
    } catch (err) {
      errorNotify(err || "Failed to save contract vehicles");
    } finally {
      setSavingAssignedVehicles(false);
    }
  };

  const handleRemoveSingleVehicle = async (cv) => {
    const vTypeId = cv.vehicle_type_id;
    const vId = cv.vehicle_id || cv.vehicle?.id;
    const allAssignedForType = safeArray(rawDetail?.contract_vehicles)
      .filter((item) => String(item.vehicle_type_id) === String(vTypeId))
      .map((item) => item.vehicle_id || item.vehicle?.id)
      .filter((vid) => vid !== vId);

    try {
      await apiService.saveContractVehicles({
        contract_id: parseIdOrRaw(id),
        vehicle_type_id: parseIdOrRaw(vTypeId),
        vehicles_ids: allAssignedForType,
      });
      successNotify(
        `Vehicle ${cv.vehicle?.registration_number || `#${vId}`} removed from contract`,
      );
      await refetchContractDetail();
    } catch (err) {
      errorNotify(err || "Failed to remove vehicle");
    }
  };

  const handleCopyRegNo = (regNo) => {
    if (!regNo) return;
    navigator.clipboard.writeText(regNo);
    setCopiedRegNo(regNo);
    setTimeout(() => setCopiedRegNo(null), 2000);
  };

  const handleAddChargeClick = () => {
    if (drawerCharge.isExisting) return;
    if (!tempChargeType || !tempChargeValue) return;
    const val = Number(tempChargeValue);

    if (tempChargeType === "per_km" || tempChargeType === "per_ltr") {
      setDrawerCharge((prev) => ({
        ...prev,
        variableType: tempChargeType,
        variableRate: val,
      }));
    } else {
      setDrawerCharge((prev) => {
        const existingFixed = prev.fixedCharges || [];
        const existsIdx = existingFixed.findIndex((f) => f.fixedType === tempChargeType);
        let nextFixed = [];
        if (existsIdx > -1) {
          nextFixed = existingFixed.map((f, idx) =>
            idx === existsIdx ? { ...f, fixedCharge: val } : f
          );
        } else {
          nextFixed = [
            ...existingFixed,
            {
              id: `fixed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              fixedType: tempChargeType,
              fixedCharge: val,
            },
          ];
        }
        return { ...prev, fixedCharges: nextFixed };
      });
    }
    setTempChargeType("");
    setTempChargeValue("");
  };

  const handleRemoveVariableCharge = () => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      variableType: "",
      variableRate: "",
      formulaTokens: (prev.formulaTokens || []).filter((t) => t.type !== "variableRate"),
    }));
  };

  const handleRemoveFixedChargeItem = (fixedId, fixedType) => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      fixedCharges: (prev.fixedCharges || []).filter((fc) => fc.id !== fixedId),
      formulaTokens: (prev.formulaTokens || []).filter(
        (t) => !(t.type === "fixedCharge" && t.metadata?.fixedType === fixedType)
      ),
    }));
  };

  const appendFormulaToken = (token) => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      formulaTokens: [...(prev.formulaTokens || []), token],
    }));
  };

  const handleRemoveFormulaToken = (tokenIdx) => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      formulaTokens: (prev.formulaTokens || []).filter((_, idx) => idx !== tokenIdx),
    }));
  };

  const handleBackspaceFormula = () => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      formulaTokens: (prev.formulaTokens || []).slice(0, -1),
    }));
  };

  const handleClearFormula = () => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      formulaTokens: [],
    }));
  };

  const handleAddHardcodedValueToFormula = () => {
    if (drawerCharge.isExisting) return;
    if (!String(drawerCharge.formulaHardcodedValue || "").trim()) return;
    appendFormulaToken(
      createFormulaToken("hardcodedValue", drawerCharge.formulaHardcodedValue, drawerCharge.formulaHardcodedValue)
    );
    setDrawerCharge((prev) => ({ ...prev, formulaHardcodedValue: "" }));
  };

  const handleAddOperatorToFormula = (operator) => {
    if (drawerCharge.isExisting) return;
    appendFormulaToken(createFormulaToken("operator", operator, operator));
  };

  const appendShortagesFormulaToken = (token) => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      shortagesFormulaTokens: [...(prev.shortagesFormulaTokens || []), token],
    }));
  };

  const handleRemoveShortagesFormulaToken = (tokenIdx) => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      shortagesFormulaTokens: (prev.shortagesFormulaTokens || []).filter((_, idx) => idx !== tokenIdx),
    }));
  };

  const handleBackspaceShortagesFormula = () => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      shortagesFormulaTokens: (prev.shortagesFormulaTokens || []).slice(0, -1),
    }));
  };

  const handleClearShortagesFormula = () => {
    if (drawerCharge.isExisting) return;
    setDrawerCharge((prev) => ({
      ...prev,
      shortagesFormulaTokens: [],
    }));
  };

  const handleAddOperatorToShortagesFormula = (operator) => {
    if (drawerCharge.isExisting) return;
    appendShortagesFormulaToken(createFormulaToken("operator", operator, operator));
  };

  const resolveFormulaTokenLabel = (token = {}) => {
    if (String(token?.type || "") === "operator") {
      return token?.label || token?.value || "";
    }

    if (String(token?.type || "") === "variableRate") {
      const val = token?.value || drawerCharge.variableRate || "";
      const typeLabel = getVariableTypeLabel(drawerCharge.variableType) || token?.label || "Variable";
      return val ? `${typeLabel} (${val})` : typeLabel;
    }

    if (String(token?.type || "") === "fixedCharge") {
      return token?.label || `Charge (${token?.value || "0"})`;
    }

    if (String(token?.type || "") === "hardcodedValue") {
      return token?.value || "0";
    }

    return token?.label || token?.value || "";
  };

  const buildFormulaDisplay = (tokens = []) =>
    (Array.isArray(tokens) ? tokens : [])
      .map((token) => resolveFormulaTokenLabel(token))
      .join(" ");

  const validateDrawerForm = (charge) => {
    const errs = {};

    if (!charge?.vehicleTypeId) {
      errs.vehicleTypeId = "Please select a Vehicle Type";
    }

    if (!charge?.numberOfVehicle || parseNumberOrZero(charge?.numberOfVehicle) <= 0) {
      errs.numberOfVehicle = "No. of Vehicles is required";
    }

    const formulaErr = validateFormulaTokens(charge?.formulaTokens || []);
    if (formulaErr) {
      errs.formulaTokens = formulaErr;
    }

    const shortagesTokens = charge?.shortagesFormulaTokens || [];
    if (shortagesTokens.length > 0) {
      const shortagesErr = validateFormulaTokens(shortagesTokens);
      if (shortagesErr) {
        errs.shortagesFormulaTokens = shortagesErr;
      }
    }

    return errs;
  };

  const validateVehicleSettingsForm = (charge) => {
    const errs = {};

    if (!charge?.vehicleTypeId) {
      errs.vehicleTypeId = "Please select a Vehicle Type";
    }

    if (!charge?.numberOfVehicle || parseNumberOrZero(charge?.numberOfVehicle) <= 0) {
      errs.numberOfVehicle = "No. of Vehicles is required";
    }

    if (
      charge?.monthlyCharges !== "" &&
      charge?.monthlyCharges !== null &&
      charge?.monthlyCharges !== undefined &&
      parseNumberOrZero(charge?.monthlyCharges) < 0
    ) {
      errs.monthlyCharges = "Monthly Rent cannot be negative";
    }

    return errs;
  };

  const handleUpdateVehicleSettings = async () => {
    const errs = validateVehicleSettingsForm(drawerCharge);
    setDrawerErrors(errs);
    setDrawerTouched(true);

    if (Object.keys(errs).length > 0) {
      const firstError = Object.values(errs)[0];
      errorNotify(firstError);
      return;
    }

    setUpdatingVehicleSetting(true);
    try {
      await apiService.updateContractVehicleSetting({
        contract_id: Number(id),
        vehicle_type_id: Number(drawerCharge.vehicleTypeId),
        no_of_vehicle: parseNumberOrZero(drawerCharge.numberOfVehicle) || 1,
        monthly_rental: parseNumberOrZero(drawerCharge.monthlyCharges),
      });

      successNotify("Vehicle settings updated successfully");
      setIsAddVehicleDrawerOpen(false);
      resetDrawerForm();
      await refetchContractDetail();
    } catch (err) {
      errorNotify(err || "Failed to update vehicle settings");
    } finally {
      setUpdatingVehicleSetting(false);
    }
  };

  const handleSaveVehicleTypeWithFormula = async () => {
    const errs = validateDrawerForm(drawerCharge);
    setDrawerErrors(errs);
    setDrawerTouched(true);

    if (Object.keys(errs).length > 0) {
      const firstError = Object.values(errs)[0];
      errorNotify(firstError);
      return;
    }

    setAddingVehicleType(true);
    try {
      const payload = {
        contract_id: Number(id),
        vehicle_type_id: Number(drawerCharge.vehicleTypeId),
        no_of_vehicle: parseNumberOrZero(drawerCharge.numberOfVehicle) || 1,
        monthly_rental: parseNumberOrZero(drawerCharge.monthlyCharges),
        charge_shortage: (drawerCharge.shortagesFormulaTokens || []).length > 0,
        variable_charge_type: drawerCharge.variableType || null,
        variable_charge_rate: parseNumberOrZero(drawerCharge.variableRate),
        contract_template_vehicle_types_charges: (drawerCharge.fixedCharges || []).map((fc) => ({
          fix_charge_type: fc.fixedType,
          fix_charge_rate: parseNumberOrZero(fc.fixedCharge),
        })),
        formula_tokens: buildFormulaPayloadTokens(drawerCharge, { tokensKey: "formulaTokens" }),
        shortages_formula_tokens: buildFormulaPayloadTokens(drawerCharge, { tokensKey: "shortagesFormulaTokens" }),
      };

      await apiService.addContractVehicleType(payload);
      successNotify("Vehicle Type with formula added successfully");
      setIsAddVehicleDrawerOpen(false);
      resetDrawerForm();
      await refetchContractDetail();
    } catch (err) {
      errorNotify(err || "Failed to add vehicle type to contract");
    } finally {
      setAddingVehicleType(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDetail = async () => {
      setPageLoading(true);

      try {
        const response = await apiService.getContractDetail(id);
        const detail = response?.data || {};

        if (!isMounted) return;
        setRawDetail(detail);
        setTemplateRecord(
          normalizePricingTemplateDetail(detail, locationTemplateRecord),
        );
      } catch (error) {
        if (!isMounted) return;

        if (locationTemplateRecord?.id) {
          setRawDetail(locationTemplateRecord);
          setTemplateRecord(
            normalizePricingTemplateDetail(
              locationTemplateRecord,
              locationTemplateRecord,
            ),
          );
        } else {
          setTemplateRecord(null);
          errorNotify(error || "Failed to fetch contract detail");
        }
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      isMounted = false;
    };
  }, [id, locationTemplateRecord]);

  const allContractVehicles = useMemo(
    () => safeArray(rawDetail?.contract_vehicles),
    [rawDetail]
  );

  const configuredVehicleTypes = useMemo(
    () =>
      safeArray(
        rawDetail?.contracts_vehicle_types ||
          rawDetail?.contract_template_vehicle_types
      ),
    [rawDetail]
  );

  const totalRequiredQuota = useMemo(
    () =>
      configuredVehicleTypes.reduce(
        (acc, cvt) => acc + parseNumberOrZero(cvt.no_of_vehicle),
        0
      ),
    [configuredVehicleTypes]
  );

  const totalAssignedCount = allContractVehicles.length;
  const fulfillmentPercentage =
    totalRequiredQuota > 0
      ? Math.min(100, Math.round((totalAssignedCount / totalRequiredQuota) * 100))
      : 0;

  const filteredContractVehicles = useMemo(() => {
    return allContractVehicles.filter((cv) => {
      const reg = (cv.vehicle?.registration_number || "").toLowerCase();
      const slug = (cv.vehicle?.registration_number_slug || "").toLowerCase();
      const typeTitle = (
        cv.vehicle?.vehicle_type?.title ||
        cv.vehicle?.vehicle_type?.name ||
        ""
      ).toLowerCase();
      const query = (vehicleSearchQuery || "").trim().toLowerCase();

      const matchesQuery =
        !query ||
        reg.includes(query) ||
        slug.includes(query) ||
        typeTitle.includes(query) ||
        String(cv.vehicle_id || "").includes(query);

      const matchesType =
        vehicleTypeFilter === "all" ||
        String(cv.vehicle_type_id) === String(vehicleTypeFilter) ||
        String(cv.vehicle?.vehicle_type?.id) === String(vehicleTypeFilter);

      return matchesQuery && matchesType;
    });
  }, [allContractVehicles, vehicleSearchQuery, vehicleTypeFilter]);

  const pricingTemplateTabs = [
    {
      key: "general",
      label: "General Information",
      icon: activeTab === "general" ? activeDetailsIcon : detailsIcon,
    },
    {
      key: "vehicles",
      label: `Contract Vehicles (${allContractVehicles.length})`,
      icon: (
        <PiTruckFill
          style={{
            fontSize: "16px",
            color: activeTab === "vehicles" ? "#4f46e5" : "#667085",
          }}
        />
      ),
    },
    {
      key: "payment",
      label: "Payment Mode",
      icon: activeTab === "payment" ? dollarActiveIcon : dollarIcon,
    },
  ];

  if (pageLoading) {
    return <SpinnerLoader />;
  }

  if (!templateRecord) {
    return (
      <div className="trip_cost_estimation_page pricing_template_details_page">
        <Breadcrumbs values={["Contract", "Contract Details"]} />
        <div className="page_header access_view">
          <h2>Contract Details</h2>
          <Button
            cancelBtn={() => navigate("/contract")}
            type="button"
            shouldRow={false}
            isShowSubmitBtn={false}
          />
        </div>
        <div className="add_trip_suggestion_div">
          <h4>Contract not found</h4>
          <p>We could not load this contract detail.</p>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={templateRecord}
      validationSchema={createContractSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await apiService.updateContract(
            id,
            buildUpdatePayload(values, rawDetail),
          );
          successNotify("Contract updated successfully");
        } catch (error) {
          errorNotify(error || "Failed to update contract");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit,
        isSubmitting,
      }) => {
        const currentCustomerOptions = prependMissingOption(customerOptions, {
          value: values.customer,
          label: values.customerLabel || values.customer,
        });
        const currentBankOptions = prependMissingOption(banksDropdownData, {
          value: values.bank,
          label: values.bankLabel || values.bank,
        });
        const currentVehicleTypeOptions = values.vehicleTypeLabels?.reduce(
          (options, item) => prependMissingOption(options, item),
          vehicleTypeDropdownOptions,
        );
        const updateAttachments = (updater) => {
          if (typeof updater === "function") {
            setFieldValue("attachments", updater(values.attachments || []));
            return;
          }

          setFieldValue("attachments", updater || []);
        };
        const isExistingVehicleType = !!drawerCharge.isExisting;
        const drawerControlsDisabled =
          isExistingVehicleType ||
          addingVehicleType ||
          updatingVehicleSetting ||
          !canUpdateTemplate;
        const vehicleSettingsDisabled =
          addingVehicleType || updatingVehicleSetting || !canUpdateTemplate;
        const vehicleSettingsUpdateDisabled =
          !isExistingVehicleType || vehicleSettingsDisabled;

        return (
          <div className="trip_cost_estimation_page pricing_template_details_page">
            <Breadcrumbs values={["Contract", "Contract Details"]} />

            <div className="page_header access_view">
              <h2>Contract Details</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate("/contract")}
                  submitBtn={handleSubmit}
                  submitText={isSubmitting ? "Saving..." : "Save Details"}
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdateTemplate}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout trip_cost_estimation_create_layout">
                <Col lg={9} style={{ minWidth: 0 }}>
                  <div className="trip_information_main_div driver_form_shell trip_cost_estimation_create_shell pricing_template_detail_shell" style={{ minWidth: 0 }}>
                    <h5>Contract</h5>

                    <div className="driver_form_content" style={{ minWidth: 0 }}>
                      <h6 className="pricing_template_system_bar">
                        <span className="d-flex align-items-center gap-1">
                          System ID:
                          <span style={{ fontWeight: 500, marginLeft: "4px" }}>
                            {values.systemId || "-"}
                          </span>
                        </span>
                      </h6>

                      <CustomTabs
                        tabs={pricingTemplateTabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />

                      {activeTab === "general" && (
                        <div className="driver_form_section pricing_template_detail_section">
                          <div className="driver_form_section_header">
                            <h3>General Information</h3>
                            <p>Main information for this contract.</p>
                          </div>

                          <Row className="pricing_template_builder_grid">
                            <Col md={12}>
                              <Input
                                label={renderFieldLabel("Contract Name")}
                                type="text"
                                placeholder="Write Contract Name"
                                name="templateName"
                                value={values.templateName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.templateName}
                                touched={touched.templateName}
                                disabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Customer")}
                                placeholder="Select Customer"
                                options={currentCustomerOptions}
                                loading={dropdownLoading}
                                value={pickOption(
                                  currentCustomerOptions,
                                  values.customer,
                                )}
                                name="customer"
                                onChange={(option) =>
                                  setFieldValue("customer", option?.value || "")
                                }
                                onBlur={handleBlur}
                                error={errors.customer}
                                touched={touched.customer}
                                isSearch
                                isDisabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Status")}
                                placeholder="Select Status"
                                options={statusOptions}
                                value={pickOption(statusOptions, values.status)}
                                name="status"
                                onChange={(option) =>
                                  setFieldValue("status", option?.value || "")
                                }
                                onBlur={handleBlur}
                                error={errors.status}
                                touched={touched.status}
                                isSearch={false}
                                isDisabled={!canUpdateTemplate}
                              />
                            </Col>



                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Effective From")}
                                type="date"
                                placeholder="YYYY/MM/DD"
                                name="effectiveFrom"
                                value={values.effectiveFrom}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.effectiveFrom}
                                touched={touched.effectiveFrom}
                                disabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Effective To")}
                                type="date"
                                placeholder="YYYY/MM/DD"
                                name="effectiveTo"
                                value={values.effectiveTo}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.effectiveTo}
                                touched={touched.effectiveTo}
                                disabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={12}>
                              <Input
                                label={renderFieldLabel("Description", false)}
                                placeholder="Write detail if any..."
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.description}
                                touched={touched.description}
                                quill
                                setFieldValue={setFieldValue}
                                className="pricing_template_description_input"
                                required={false}
                                disabled={!canUpdateTemplate}
                              />
                            </Col>
                          </Row>

                          <div
                            className="driver_form_section_header d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row gap-2 flex-wrap"
                            style={{ marginTop: "32px", marginBottom: "16px" }}
                          >
                            <div>
                              <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", margin: 0 }}>
                                Vehicle Types & Billing Rules
                              </h3>
                              <p style={{ fontSize: "12px", color: "#6b7280", margin: "2px 0 0 0" }}>
                                Manage vehicle allocation, monthly rental, and revenue and shortage formulas for this contract.
                              </p>
                            </div>

                            {canUpdateTemplate && (
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                {unselectedVehicleTypeOptions.length > 0 ? (
                                  <>
                                    <div style={{ minWidth: "200px", maxWidth: "240px" }} className="header-vehicle-select-box">
                                      <Select
                                        label=""
                                        required={false}
                                        placeholder="Select Vehicle Type"
                                        options={unselectedVehicleTypeOptions}
                                        value={pickOption(unselectedVehicleTypeOptions, selectedAddVehicleType)}
                                        onChange={(option) => setSelectedAddVehicleType(option?.value || "")}
                                        isSearch={true}
                                        className="m-0 mb-0 p-0"
                                        customStyles={{
                                          control: (base) => ({
                                            ...base,
                                            minHeight: "36px",
                                            height: "36px",
                                          }),
                                        }}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-primary"
                                      style={{
                                        backgroundColor: "#2563eb",
                                        borderColor: "#2563eb",
                                        height: "38px",
                                        padding: "0 14px",
                                        borderRadius: "6px",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        whiteSpace: "nowrap",
                                        marginBottom: "0px",
                                      }}
                                      onClick={() => {
                                        if (!selectedAddVehicleType) return;
                                        const vtOpt = vehicleTypeDropdownOptions.find(
                                          (o) => String(o.value) === String(selectedAddVehicleType)
                                        ) || { value: selectedAddVehicleType, label: selectedAddVehicleType };
                                        setDrawerCharge(createDefaultDrawerCharge(vtOpt));
                                        setIsAddVehicleDrawerOpen(true);
                                      }}
                                      disabled={!selectedAddVehicleType}
                                    >
                                      <PiPlus style={{ fontSize: "14px" }} /> Add Vehicle Type
                                    </button>
                                  </>
                                ) : (
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      backgroundColor: "#f3f4f6",
                                      padding: "6px 12px",
                                      borderRadius: "6px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    All vehicle types added
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Vehicle Type Cards Container (2 per row on desktop) */}
                          <div className="row g-3 mb-4">
                            {Array.isArray(rawDetail?.contracts_vehicle_types || rawDetail?.contract_template_vehicle_types) &&
                            (rawDetail?.contracts_vehicle_types || rawDetail?.contract_template_vehicle_types).length > 0 ? (
                              (rawDetail?.contracts_vehicle_types || rawDetail?.contract_template_vehicle_types).map((cvt, idx) => {
                                const vtId = cvt.vehicle_type_id || cvt.vehicle_type?.id;
                                const vtName =
                                  cvt.vehicle_type?.title ||
                                  cvt.vehicle_type?.name ||
                                  pickOption(vehicleTypeDropdownOptions, vtId)?.label ||
                                  `Vehicle Type ${vtId}`;

                                const cvtVehicles = allContractVehicles.filter(
                                  (cv) => String(cv.vehicle_type_id) === String(vtId)
                                );
                                const assignedCount = cvtVehicles.length;
                                const quotaCount = parseNumberOrZero(cvt.no_of_vehicle);
                                const isQuotaFulfilled = quotaCount > 0 && assignedCount === quotaCount;
                                const isOverQuota = quotaCount > 0 && assignedCount > quotaCount;
                                const isBelowQuota = quotaCount > 0 && assignedCount < quotaCount;

                                const fixedChargesList = safeArray(cvt.charges || cvt.contract_template_vehicle_types_charges);

                                return (
                                  <div key={cvt.contract_template_vehicle_type_id || cvt.id || idx} className="col-12 col-lg-6">
                                    <div
                                      className="card h-100 shadow-sm"
                                      style={{
                                        backgroundColor: "#ffffff",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "10px",
                                        padding: "14px 16px",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                                      }}
                                    >
                                      <div>
                                        {/* 1. Card Header */}
                                        <div className="d-flex justify-content-between align-items-start gap-2 pb-2 mb-2 border-bottom">
                                          <div className="d-flex align-items-center gap-2">
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "6px",
                                                backgroundColor: "#eff6ff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#2563eb",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <PiTruckFill style={{ fontSize: "17px" }} />
                                            </div>
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "14px",
                                                  fontWeight: 700,
                                                  color: "#0f172a",
                                                  lineHeight: "1.2",
                                                }}
                                              >
                                                {vtName}
                                              </div>
                                              {fixedChargesList.length > 0 && (
                                                <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                                                  Fixed:{" "}
                                                  {fixedChargesList
                                                    .map(
                                                      (fc) =>
                                                        `${getFixedTypeLabel(fc.fix_charge_type || fc.type)} (${fc.fix_charge_rate || fc.rate || 0})`
                                                    )
                                                    .join(", ")}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Header Actions */}
                                          {canUpdateTemplate && (
                                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                              <button
                                                type="button"
                                                className="btn btn-sm"
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "3px",
                                                  fontSize: "11px",
                                                  fontWeight: 600,
                                                  padding: "3px 8px",
                                                  borderRadius: "5px",
                                                  border: "1px solid #d1d5db",
                                                  backgroundColor: "#ffffff",
                                                  color: "#374151",
                                                }}
                                                onClick={() => handleEditVehicleTypeFormula(cvt)}
                                                title="Configure Formula"
                                              >
                                                <PiPencilSimple style={{ fontSize: "12px" }} /> Formula
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm"
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "3px",
                                                  fontSize: "11px",
                                                  fontWeight: 600,
                                                  padding: "3px 8px",
                                                  borderRadius: "5px",
                                                  border: "1px solid #bfdbfe",
                                                  backgroundColor: "#eff6ff",
                                                  color: "#2563eb",
                                                }}
                                                onClick={() => handleOpenAssignModal(vtId)}
                                                title="Manage Vehicles"
                                              >
                                                <PiTruck style={{ fontSize: "13px" }} /> Manage
                                              </button>
                                            </div>
                                          )}
                                        </div>

                                        {/* 2. Compact Allocation & Rental Summary Strip */}
                                        <div
                                          style={{
                                            backgroundColor: "#f8fafc",
                                            borderRadius: "6px",
                                            border: "1px solid #f1f5f9",
                                            padding: "8px 10px",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          <div className="row g-2 align-items-center text-center text-sm-start">
                                            <div className="col-4">
                                              <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                                Quota
                                              </div>
                                              <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginTop: "1px" }}>
                                                {cvt.no_of_vehicle ?? "-"}
                                              </div>
                                            </div>

                                            <div className="col-4 border-start border-end px-2">
                                              <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                                Assigned
                                              </div>
                                              <div className="d-flex align-items-center justify-content-center justify-content-sm-start gap-1 flex-wrap" style={{ marginTop: "1px" }}>
                                                <span style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                                                  {assignedCount}
                                                </span>

                                                {/* Status Badge */}
                                                {isOverQuota && (
                                                  <span
                                                    style={{
                                                      fontSize: "10px",
                                                      fontWeight: 600,
                                                      backgroundColor: "#fef3c7",
                                                      color: "#b45309",
                                                      border: "1px solid #fde68a",
                                                      borderRadius: "10px",
                                                      padding: "1px 5px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "2px",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    <PiWarningCircle style={{ fontSize: "10px" }} /> +{assignedCount - quotaCount} Over
                                                  </span>
                                                )}

                                                {isQuotaFulfilled && (
                                                  <span
                                                    style={{
                                                      fontSize: "10px",
                                                      fontWeight: 600,
                                                      backgroundColor: "#ecfdf5",
                                                      color: "#059669",
                                                      border: "1px solid #a7f3d0",
                                                      borderRadius: "10px",
                                                      padding: "1px 5px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "2px",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    <PiCheckCircle style={{ fontSize: "10px" }} /> Met
                                                  </span>
                                                )}

                                                {isBelowQuota && (
                                                  <span
                                                    style={{
                                                      fontSize: "10px",
                                                      fontWeight: 600,
                                                      backgroundColor: "#eff6ff",
                                                      color: "#2563eb",
                                                      border: "1px solid #bfdbfe",
                                                      borderRadius: "10px",
                                                      padding: "1px 5px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "2px",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    -{quotaCount - assignedCount} Left
                                                  </span>
                                                )}

                                                {quotaCount === 0 && (
                                                  <span
                                                    style={{
                                                      fontSize: "10px",
                                                      fontWeight: 600,
                                                      backgroundColor: "#f1f5f9",
                                                      color: "#475467",
                                                      borderRadius: "10px",
                                                      padding: "1px 5px",
                                                    }}
                                                  >
                                                    No quota
                                                  </span>
                                                )}
                                              </div>
                                            </div>

                                            <div className="col-4">
                                              <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                                Rental
                                              </div>
                                              <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginTop: "1px" }}>
                                                {cvt.monthly_rental ?? "0.00"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* 3. Assigned Vehicles Section */}
                                        <div style={{ marginBottom: "10px" }}>
                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span style={{ fontSize: "11px", fontWeight: 600, color: "#475467" }}>
                                              Assigned Vehicles ({assignedCount})
                                            </span>
                                            {canUpdateTemplate && (
                                              <button
                                                type="button"
                                                onClick={() => handleOpenAssignModal(vtId)}
                                                style={{
                                                  background: "none",
                                                  border: "none",
                                                  padding: "0",
                                                  fontSize: "11px",
                                                  color: "#2563eb",
                                                  fontWeight: 600,
                                                  cursor: "pointer",
                                                  textDecoration: "underline",
                                                }}
                                              >
                                                {assignedCount > 0 ? "Manage" : "+ Assign"}
                                              </button>
                                            )}
                                          </div>

                                          <div className="d-flex align-items-center gap-1 flex-wrap">
                                            {assignedCount > 0 ? (
                                              <>
                                                {cvtVehicles.slice(0, 4).map((cv) => (
                                                  <span
                                                    key={cv.id || cv.vehicle_id}
                                                    onClick={() => {
                                                      setVehicleTypeFilter(String(vtId));
                                                      setActiveTab("vehicles");
                                                    }}
                                                    title="Click to view in Contract Vehicles tab"
                                                    style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                      padding: "2px 7px",
                                                      borderRadius: "4px",
                                                      fontSize: "11px",
                                                      fontFamily: "monospace",
                                                      fontWeight: 700,
                                                      backgroundColor: "#f8fafc",
                                                      color: "#0f172a",
                                                      border: "1px solid #cbd5e1",
                                                      cursor: "pointer",
                                                      transition: "background-color 0.15s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.backgroundColor = "#eff6ff";
                                                      e.currentTarget.style.borderColor = "#93c5fd";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.currentTarget.style.backgroundColor = "#f8fafc";
                                                      e.currentTarget.style.borderColor = "#cbd5e1";
                                                    }}
                                                  >
                                                    <PiTruckFill style={{ fontSize: "11px", color: "#2563eb" }} />
                                                    {cv.vehicle?.registration_number?.toUpperCase() || `#${cv.vehicle_id}`}
                                                  </span>
                                                ))}

                                                {cvtVehicles.length > 4 && (
                                                  <span
                                                    onClick={() => {
                                                      setVehicleTypeFilter(String(vtId));
                                                      setActiveTab("vehicles");
                                                    }}
                                                    title="Click to view all assigned vehicles"
                                                    style={{
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      padding: "2px 6px",
                                                      borderRadius: "4px",
                                                      fontSize: "10px",
                                                      fontWeight: 600,
                                                      backgroundColor: "#f1f5f9",
                                                      color: "#475467",
                                                      cursor: "pointer",
                                                      border: "1px solid #e2e8f0",
                                                    }}
                                                  >
                                                    +{cvtVehicles.length - 4} more
                                                  </span>
                                                )}
                                              </>
                                            ) : (
                                              <span style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic" }}>
                                                No vehicles assigned
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      {/* 4. Formula Configuration Section */}
                                      <div className="d-flex flex-column gap-2 pt-2 border-top mt-1">
                                        {/* Revenue Formula */}
                                        <div
                                          style={{
                                            backgroundColor: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "6px",
                                            padding: "6px 9px",
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span
                                              style={{
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                color: "#64748b",
                                                letterSpacing: "0.4px",
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Revenue Formula
                                            </span>
                                            {canUpdateTemplate && (
                                              <button
                                                type="button"
                                                onClick={() => handleEditVehicleTypeFormula(cvt)}
                                                style={{
                                                  background: "none",
                                                  border: "none",
                                                  padding: "0",
                                                  fontSize: "10px",
                                                  color: "#2563eb",
                                                  fontWeight: 600,
                                                  cursor: "pointer",
                                                }}
                                              >
                                                Edit
                                              </button>
                                            )}
                                          </div>
                                          <div>
                                            {renderFormulaBadges(cvt.formula_tokens)}
                                          </div>
                                        </div>

                                        {/* Shortage Formula */}
                                        <div
                                          style={{
                                            backgroundColor: "#f8fafc",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: "6px",
                                            padding: "6px 9px",
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center mb-1">
                                            <span
                                              style={{
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                color: "#64748b",
                                                letterSpacing: "0.4px",
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              Shortage Formula
                                            </span>
                                            {cvt.charge_shortage ? (
                                              <span
                                                style={{
                                                  padding: "1px 5px",
                                                  borderRadius: "10px",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  backgroundColor: "#ecfdf5",
                                                  color: "#059669",
                                                  border: "1px solid #a7f3d0",
                                                }}
                                              >
                                                Enabled
                                              </span>
                                            ) : (
                                              <span
                                                style={{
                                                  padding: "1px 5px",
                                                  borderRadius: "10px",
                                                  fontSize: "10px",
                                                  fontWeight: 600,
                                                  backgroundColor: "#f1f5f9",
                                                  color: "#64748b",
                                                  border: "1px solid #e2e8f0",
                                                }}
                                              >
                                                Disabled
                                              </span>
                                            )}
                                          </div>
                                          <div>
                                            {cvt.charge_shortage ? (
                                              renderFormulaBadges(
                                                cvt.shortage_formula_tokens || cvt.shortages_formula_tokens
                                              )
                                            ) : (
                                              <span style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic" }}>
                                                Shortage calculation is disabled.
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="col-12">
                                <div
                                  style={{
                                    backgroundColor: "#ffffff",
                                    border: "1px dashed #cbd5e1",
                                    borderRadius: "10px",
                                    padding: "28px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      backgroundColor: "#eff6ff",
                                      color: "#2563eb",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "20px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    <PiTruckFill />
                                  </div>
                                  <div style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", margin: "0 0 4px 0" }}>
                                    No Vehicle Types Configured
                                  </div>
                                  <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                                    Add vehicle types above to configure quotas, rentals, and billing formulas.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {activeTab === "vehicles" && (
                        <div className="driver_form_section pricing_template_detail_section">
                          <div className="driver_form_section_header mb-3">
                            <div>
                              <h3>Assigned Contract Vehicles</h3>
                              <p>Review, monitor, and manage the fleet vehicles assigned to this contract.</p>
                            </div>
                          </div>



                          {/* Search & Filter Toolbar */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              flexWrap: "wrap",
                              gap: "12px",
                              marginBottom: "16px",
                              padding: "12px 16px",
                              backgroundColor: "#f8fafc",
                              border: "1px solid #eaecf0",
                              borderRadius: "8px",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", flex: 1, minWidth: "280px" }}>
                              {/* Search input */}
                              <div style={{ position: "relative", minWidth: "260px", flex: 1 }}>
                                <PiMagnifyingGlass
                                  style={{
                                    position: "absolute",
                                    left: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: "#94a3b8",
                                    fontSize: "16px",
                                    pointerEvents: "none",
                                  }}
                                />
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search vehicle plate (e.g. TMU 500) or type..."
                                  value={vehicleSearchQuery}
                                  onChange={(e) => setVehicleSearchQuery(e.target.value)}
                                  style={{
                                    paddingLeft: "36px",
                                    height: "38px",
                                    fontSize: "13px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "#ffffff",
                                  }}
                                />
                                {vehicleSearchQuery && (
                                  <button
                                    type="button"
                                    onClick={() => setVehicleSearchQuery("")}
                                    style={{
                                      position: "absolute",
                                      right: "10px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      border: "none",
                                      background: "none",
                                      color: "#94a3b8",
                                      cursor: "pointer",
                                      padding: 0,
                                    }}
                                  >
                                    <PiX />
                                  </button>
                                )}
                              </div>

                              {/* Vehicle type filter */}
                              <div style={{ minWidth: "200px" }}>
                                <select
                                  className="form-select"
                                  value={vehicleTypeFilter}
                                  onChange={(e) => setVehicleTypeFilter(e.target.value)}
                                  style={{
                                    height: "38px",
                                    fontSize: "13px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "#ffffff",
                                    cursor: "pointer",
                                  }}
                                >
                                  <option value="all">All Vehicle Types ({allContractVehicles.length})</option>
                                  {configuredVehicleTypes.map((cvt) => {
                                    const vtId = cvt.vehicle_type_id || cvt.vehicle_type?.id;
                                    const vtTitle = cvt.vehicle_type?.title || cvt.vehicle_type?.name || `Vehicle Type ${vtId}`;
                                    const count = allContractVehicles.filter((cv) => String(cv.vehicle_type_id) === String(vtId)).length;
                                    return (
                                      <option key={vtId} value={String(vtId)}>
                                        {vtTitle} ({count})
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                            </div>

                            <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500 }}>
                              Showing <strong style={{ color: "#0f172a" }}>{filteredContractVehicles.length}</strong> of{" "}
                              <strong style={{ color: "#0f172a" }}>{allContractVehicles.length}</strong> assigned vehicles
                            </div>
                          </div>

                            {/* Vehicles Table */}
                            <div className="table-responsive" style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #eaecf0", backgroundColor: "#ffffff" }}>
                              <table className="table" style={{ width: "100%", minWidth: "600px", borderCollapse: "collapse", margin: 0 }}>
                                <thead>
                                  <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #eaecf0", textAlign: "left" }}>
                                    <th style={{ padding: "12px 16px", color: "#475467", fontWeight: 600, fontSize: "12px", width: "50px" }}>#</th>
                                    <th style={{ padding: "12px 16px", color: "#475467", fontWeight: 600, fontSize: "12px", minWidth: "220px" }}>VEHICLE REGISTRATION</th>
                                    <th style={{ padding: "12px 16px", color: "#475467", fontWeight: 600, fontSize: "12px", minWidth: "200px" }}>VEHICLE TYPE</th>
                                    <th style={{ padding: "12px 16px", color: "#475467", fontWeight: 600, fontSize: "12px", minWidth: "140px" }}>STATUS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredContractVehicles.length > 0 ? (
                                    filteredContractVehicles.map((cv, idx) => {
                                      const regNo = cv.vehicle?.registration_number?.toUpperCase() || (cv.vehicle_id ? `#${cv.vehicle_id}` : "-");
                                      const vtTitle = cv.vehicle?.vehicle_type?.title || cv.vehicle?.vehicle_type?.name || pickOption(vehicleTypeDropdownOptions, cv.vehicle_type_id)?.label || `Type #${cv.vehicle_type_id}`;
                                      const isCopied = copiedRegNo === regNo;

                                      return (
                                        <tr key={cv.id || `${cv.vehicle_id}-${idx}`} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                          <td style={{ padding: "14px 16px", color: "#64748b", fontSize: "13px" }}>{idx + 1}</td>
                                          <td style={{ padding: "14px 16px" }}>
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                                              <div
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "stretch",
                                                  border: "1.5px solid #1e293b",
                                                  borderRadius: "5px",
                                                  overflow: "hidden",
                                                  backgroundColor: "#ffffff",
                                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#1e3a8a",
                                                    color: "#ffffff",
                                                    fontSize: "9px",
                                                    fontWeight: 800,
                                                    padding: "3px 5px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    letterSpacing: "0.5px",
                                                  }}
                                                >
                                                  PK
                                                </div>
                                                <div
                                                  style={{
                                                    padding: "3px 10px",
                                                    fontFamily: "monospace, 'SF Pro Display', sans-serif",
                                                    fontWeight: 800,
                                                    fontSize: "13px",
                                                    letterSpacing: "1px",
                                                    color: "#0f172a",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                  }}
                                                >
                                                  {regNo}
                                                </div>
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => handleCopyRegNo(regNo)}
                                                title="Copy registration number"
                                                style={{
                                                  border: "none",
                                                  background: "none",
                                                  color: isCopied ? "#10b981" : "#94a3b8",
                                                  cursor: "pointer",
                                                  padding: "4px",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  fontSize: "14px",
                                                }}
                                              >
                                                {isCopied ? <PiCheck /> : <PiCopy />}
                                              </button>
                                            </div>
                                          </td>
                                          <td style={{ padding: "14px 16px" }}>
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "4px 10px", borderRadius: "6px", backgroundColor: "#f1f5f9", color: "#334155", fontSize: "12px", fontWeight: 600 }}>
                                              <PiTruckFill style={{ color: "#4f46e5", fontSize: "14px" }} />
                                              {vtTitle}
                                            </div>
                                          </td>
                                          <td style={{ padding: "14px 16px" }}>
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                padding: "3px 8px",
                                                borderRadius: "12px",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                backgroundColor: "#ecfdf5",
                                                color: "#059669",
                                                border: "1px solid #a7f3d0",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  width: "6px",
                                                  height: "6px",
                                                  borderRadius: "50%",
                                                  backgroundColor: "#10b981",
                                                  display: "inline-block",
                                                }}
                                              />
                                              Active in Contract
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td colSpan={4} style={{ padding: "40px 20px", textAlign: "center" }}>
                                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                        <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "#94a3b8" }}>
                                          <PiTruck />
                                        </div>
                                        <h5 style={{ margin: 0, fontWeight: 600, color: "#1e293b", fontSize: "15px" }}>
                                          {vehicleSearchQuery || vehicleTypeFilter !== "all" ? "No matching vehicles found" : "No Vehicles Assigned Yet"}
                                        </h5>
                                        <p style={{ margin: 0, color: "#64748b", fontSize: "13px", maxWidth: "420px" }}>
                                          {vehicleSearchQuery || vehicleTypeFilter !== "all"
                                            ? "Try adjusting your search terms or filters to find what you are looking for."
                                            : "Assign vehicles from your fleet to this contract to manage allocations and track operations."}
                                        </p>
                                        {canUpdateTemplate && configuredVehicleTypes.length > 0 && !vehicleSearchQuery && vehicleTypeFilter === "all" && (
                                          <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            style={{
                                              backgroundColor: "#4f46e5",
                                              borderColor: "#4f46e5",
                                              marginTop: "6px",
                                              fontWeight: 600,
                                            }}
                                            onClick={() => handleOpenAssignModal()}
                                          >
                                            <PiPlus /> Assign Vehicles Now
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {activeTab === "payment" && (
                        <div className="driver_form_section pricing_template_detail_section">
                          <div className="driver_form_section_header">
                            <h3>Payment Mode</h3>
                            <p>Contract billing and settlement settings.</p>
                          </div>

                          <Row className="pricing_template_builder_grid">
                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Billing Frequency")}
                                placeholder="Select Billing Frequency"
                                options={billingFrequencyOptions}
                                value={pickOption(
                                  billingFrequencyOptions,
                                  values.billingFrequency,
                                )}
                                name="billingFrequency"
                                onChange={(option) =>
                                  setFieldValue(
                                    "billingFrequency",
                                    option?.value || "",
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.billingFrequency}
                                touched={touched.billingFrequency}
                                isSearch={false}
                                isDisabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Payment Method")}
                                placeholder="Select Payment Method"
                                options={paymentMethodOptions}
                                value={pickOption(
                                  paymentMethodOptions,
                                  values.paymentMethod,
                                )}
                                name="paymentMethod"
                                onChange={(option) => {
                                  const nextPaymentMethod = option?.value || "";
                                  setFieldValue(
                                    "paymentMethod",
                                    nextPaymentMethod,
                                  );

                                  if (nextPaymentMethod !== "bank_transfer") {
                                    setFieldValue("bank", "", false);
                                    setFieldValue("accountTitle", "", false);
                                    setFieldValue("iban", "", false);
                                  }
                                }}
                                onBlur={handleBlur}
                                error={errors.paymentMethod}
                                touched={touched.paymentMethod}
                                isSearch={false}
                                isDisabled={!canUpdateTemplate}
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Payment Terms (Days)")}
                                placeholder="Select Payment Terms"
                                options={paymentTermsOptions}
                                value={pickOption(
                                  paymentTermsOptions,
                                  values.paymentTerms,
                                )}
                                name="paymentTerms"
                                onChange={(option) =>
                                  setFieldValue(
                                    "paymentTerms",
                                    option?.value || "",
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.paymentTerms}
                                touched={touched.paymentTerms}
                                isSearch={false}
                                isDisabled={!canUpdateTemplate}
                              />
                            </Col>

                            {values.paymentMethod === "bank_transfer" && (
                              <>
                                <Col md={6}>
                                  <Select
                                    label={renderFieldLabel("Bank")}
                                    placeholder="Select Bank"
                                    options={currentBankOptions}
                                    loading={dropdownLoading}
                                    value={pickOption(
                                      currentBankOptions,
                                      values.bank,
                                    )}
                                    name="bank"
                                    onChange={(option) =>
                                      setFieldValue("bank", option?.value || "")
                                    }
                                    onBlur={handleBlur}
                                    error={errors.bank}
                                    touched={touched.bank}
                                    isSearch={false}
                                    isDisabled={!canUpdateTemplate}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel("Account Title")}
                                    type="text"
                                    placeholder="XYZ Company Ltd."
                                    name="accountTitle"
                                    value={values.accountTitle}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.accountTitle}
                                    touched={touched.accountTitle}
                                    disabled={!canUpdateTemplate}
                                  />
                                </Col>

                                <Col md={6}>
                                  <Input
                                    label={renderFieldLabel(
                                      "IBAN/Account Number",
                                    )}
                                    type="text"
                                    placeholder="PK00XXXX"
                                    name="iban"
                                    value={values.iban}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.iban}
                                    touched={touched.iban}
                                    disabled={!canUpdateTemplate}
                                  />
                                </Col>
                              </>
                            )}
                          </Row>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>

                <Col lg={3}>
                  <div className="driver_form_sidebar trip_cost_estimation_create_sidebar">
                    <div className="trip_notes_main_div create_vehicle_notes driver_notes_card trip_cost_estimation_notes_card">
                      <h5>Notes</h5>
                      <ReactQuill
                        placeholder="Notes if any..."
                        modules={modules}
                        formats={formats}
                        value={values.notes || ""}
                        onChange={(content) => setFieldValue("notes", content)}
                        readOnly={!canUpdateTemplate}
                        className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                      />
                      {touched.notes && errors.notes ? (
                        <div
                          className="error-message"
                          style={{
                            color: "#ff0000de",
                            fontSize: "12px",
                            margin: "8px 12px 0",
                          }}
                        >
                          {errors.notes}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <Drawer
              placement="right"
              onClose={() => {
                setIsAddVehicleDrawerOpen(false);
                resetDrawerForm();
              }}
              open={isAddVehicleDrawerOpen}
              width={850}
              closable={false}
              className="formula-builder-drawer"
              title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <div>
                    <div className="formula-drawer-header-title">
                      <h4 style={{ margin: 0, fontWeight: 700, fontSize: "20px" }}>Revenue Formula Builder</h4>
                      {drawerCharge.vehicleType && <span className="vehicle-type-badge">{drawerCharge.vehicleType}</span>}
                    </div>
                    <div className="formula-drawer-header-subtitle">
                      Define charges, compose the revenue formula and test it with sample trip data
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddVehicleDrawerOpen(false);
                      resetDrawerForm();
                    }}
                    style={{
                      border: "none",
                      background: "#f3f4f6",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#4b5563",
                      padding: 0,
                    }}
                  >
                    <PiXBold style={{ fontSize: "16px" }} />
                  </button>
                </div>
              }
              footer={
                <div className="drawer-footer-actions">
                  <button
                    type="button"
                    className="footer-btn-back"
                    onClick={() => {
                      setIsAddVehicleDrawerOpen(false);
                      resetDrawerForm();
                    }}
                  >
                    <PiArrowLeft /> Back
                  </button>
                  <button
                    type="button"
                    className="footer-btn-save"
                    onClick={isExistingVehicleType ? handleUpdateVehicleSettings : handleSaveVehicleTypeWithFormula}
                    disabled={addingVehicleType || updatingVehicleSetting || !canUpdateTemplate}
                    style={{
                      opacity: (addingVehicleType || updatingVehicleSetting || !canUpdateTemplate) ? 0.6 : 1,
                      cursor: (addingVehicleType || updatingVehicleSetting || !canUpdateTemplate) ? "not-allowed" : "pointer",
                    }}
                  >
                    {addingVehicleType ? "Saving..." : updatingVehicleSetting ? "Updating..." : isExistingVehicleType ? "Update Settings" : "Save Formula"}
                  </button>
                </div>
              }
            >
              <style>{`
                .formula-builder-drawer .ant-drawer-content-wrapper {
                  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.08) !important;
                }
                .formula-builder-drawer .ant-drawer-header {
                  border-bottom: 1px solid #f3f4f6 !important;
                  padding: 20px 24px !important;
                }
                .formula-builder-drawer .ant-drawer-body {
                  background-color: #f9fafb !important;
                  padding: 24px !important;
                  display: flex;
                  flex-direction: column;
                  gap: 24px;
                  overflow-y: auto;
                }
                .formula-drawer-header-title {
                  display: flex;
                  align-items: center;
                  gap: 12px;
                }
                .vehicle-type-badge {
                  background-color: #eff6ff;
                  color: #2563eb;
                  font-size: 12px;
                  font-weight: 600;
                  padding: 4px 10px;
                  border-radius: 4px;
                  border: 1px solid #bfdbfe;
                }
                .formula-drawer-header-subtitle {
                  font-size: 13px;
                  color: #6b7280;
                  margin-top: 4px;
                  font-weight: 400;
                }
                .formula-drawer-card {
                  background: #ffffff;
                  border: 1px solid #e5e7eb;
                  border-radius: 6px;
                  padding: 24px;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                }
                .formula-drawer-card-header {
                  display: flex;
                  align-items: flex-start;
                  margin-bottom: 20px;
                }
                .formula-drawer-step-badge {
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  width: 26px;
                  height: 26px;
                  background-color: #eff6ff;
                  color: #2563eb;
                  font-weight: 700;
                  font-size: 13px;
                  border-radius: 4px;
                  margin-right: 12px;
                  flex-shrink: 0;
                }
                .formula-drawer-card-title-group h5 {
                  font-size: 16px;
                  font-weight: 700;
                  color: #1f2937;
                  margin: 0;
                }
                .formula-drawer-card-title-group p {
                  font-size: 13px;
                  color: #6b7280;
                  margin: 4px 0 0 0;
                }
                .formula-drawer-grid-row {
                  display: flex;
                  gap: 12px;
                  align-items: flex-end;
                  flex-wrap: wrap;
                  margin-bottom: 16px;
                }
                .formula-drawer-grid-col {
                  flex: 1;
                  min-width: 150px;
                }
                .formula-drawer-grid-col-btn {
                  flex-shrink: 0;
                }
                .add-charge-btn {
                  background-color: #2563eb;
                  color: #ffffff;
                  border: none;
                  height: 38px;
                  border-radius: 4px;
                  font-weight: 600;
                  font-size: 13px;
                  padding: 0 16px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                  cursor: pointer;
                  transition: all 0.2s ease;
                }
                .add-charge-btn:hover {
                  background-color: #1d4ed8;
                }
                .charges-builder-group {
                  display: flex;
                  gap: 16px;
                  margin-bottom: 20px;
                  align-items: stretch;
                }
                .component-composer-box {
                  flex: 1.5;
                  border: 1px solid #e5e7eb;
                  border-radius: 6px;
                  padding: 16px;
                  background-color: #f9fafb;
                }
                .vehicle-settings-box {
                  flex: 1;
                  border: 1px solid #e5e7eb;
                  border-radius: 6px;
                  padding: 16px;
                  background-color: #f9fafb;
                }
                .sub-group-title {
                  font-size: 11px;
                  font-weight: 700;
                  color: #4b5563;
                  margin-top: 0;
                  margin-bottom: 12px;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }
                .empty-placeholder {
                  border: 1px dashed #d1d5db;
                  border-radius: 4px;
                  padding: 20px;
                  text-align: center;
                  color: #9ca3af;
                  font-size: 13px;
                  background-color: #fafafa;
                }
                .added-components-card {
                  border: 1px solid #e5e7eb;
                  background-color: #f9fafb;
                  padding: 16px;
                  border-radius: 6px;
                  margin-top: 12px;
                }
                .active-components-container {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 12px;
                  margin-top: 8px;
                }
                .pricing-component-card {
                  background: #ffffff;
                  border: 1px solid #e5e7eb;
                  border-radius: 6px;
                  padding: 12px 16px;
                  width: calc(33.33% - 8px);
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
                  display: flex;
                  flex-direction: column;
                  gap: 6px;
                  position: relative;
                }
                .pricing-component-card .card-top-row {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  width: 100%;
                }
                .pricing-component-card .card-badge-name {
                  font-size: 11px;
                  font-weight: 700;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }
                .pricing-component-card.is-variable .card-badge-name { color: #10b981; }
                .pricing-component-card.is-fixed .card-badge-name { color: #2563eb; }
                .pricing-component-card .card-remove-btn {
                  background: none; border: none; cursor: pointer; color: #9ca3af; padding: 0; display: inline-flex; align-items: center;
                }
                .pricing-component-card .card-middle-row { font-size: 16px; font-weight: 700; color: #1f2937; }
                .pricing-component-card .card-bottom-row { font-size: 12px; color: #6b7280; }
                .category-tab-bar { display: flex; gap: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 12px; margin-bottom: 16px; }
                .category-tab-btn {
                  display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border-radius: 4px; font-size: 13px; font-weight: 500; background-color: #ffffff; border: 1px solid #e5e7eb; color: #4b5563; cursor: pointer; transition: all 0.2s;
                }
                .category-tab-btn.is-active { border-color: #2563eb; background-color: #eff6ff; color: #2563eb; }
                .category-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
                .dot-trip-data { background-color: #3b82f6; }
                .dot-charges { background-color: #10b981; }
                .dot-constants { background-color: #8b5cf6; }
                .dot-operators { background-color: #f59e0b; }
                .tab-popover-wrapper { position: relative; display: inline-block; }
                .composer-popover-dropdown {
                  position: absolute; top: 100%; left: 0; background-color: #ffffff; border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); z-index: 100; min-width: 300px; display: flex; flex-direction: column;
                }
                .popover-heading { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; display: block; margin-bottom: 8px; }
                .popover-grid { display: flex; flex-direction: column; gap: 6px; max-height: 200px; overflow-y: auto; }
                .popover-grid-item { background: none; border: none; color: #374151; padding: 8px 12px; font-size: 13px; font-weight: 500; text-align: left; border-radius: 4px; cursor: pointer; }
                .popover-grid-item:hover { background-color: #f3f4f6; }
                .popover-text-input { flex: 1; height: 36px; border: 1px solid #d1d5db; border-radius: 4px; padding: 0 12px; font-size: 13px; outline: none; }
                .popover-add-btn { background-color: #2563eb; color: #ffffff; border: none; padding: 0 16px; height: 36px; border-radius: 4px; font-weight: 600; font-size: 13px; cursor: pointer; }
                .popover-operators-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
                .popover-operator-btn { background-color: #ffffff; border: 1px solid #e5e7eb; color: #374151; height: 38px; font-size: 15px; font-weight: 600; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
                .formula-composer-workspace { border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; background-color: #ffffff; min-height: 120px; display: flex; flex-direction: column; justify-content: space-between; }
                .composer-tokens-list { display: flex; flex-wrap: wrap; gap: 6px; }
                .composer-token-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; }
                .composer-token-pill.type-tripData { background-color: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
                .composer-token-pill.type-variableRate { background-color: #e0f2fe; border: 1px solid #bae6fd; color: #0369a1; }
                .composer-token-pill.type-fixedCharge { background-color: #f0fdf4; border: 1px solid #bbf7d0; color: #166534; }
                .composer-token-pill.type-hardcodedValue { background-color: #f5f3ff; border: 1px solid #ddd6fe; color: #5b21b6; }
                .composer-token-pill.type-operator { background-color: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
                .composer-token-pill:hover { opacity: 0.8; text-decoration: line-through; }
                .composer-workspace-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; border-top: 1px solid #f3f4f6; padding-top: 12px; }
                .action-btn-outline { border: 1px solid #d1d5db; background: #ffffff; color: #374151; padding: 6px 12px; border-radius: 4px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; }
                .live-preview-section { margin-top: 20px; }
                .live-preview-label { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
                .live-preview-box { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 16px; font-family: monospace; font-size: 14px; color: #1f2937; font-weight: 500; min-height: 54px; }
                .formula-error-alert { margin-top: 12px; display: flex; align-items: center; gap: 8px; padding: 10px 14px; background-color: #fef2f2; border: 1px solid #fee2e2; border-radius: 6px; color: #b91c1c; font-size: 13px; font-weight: 500; }
                .formula-success-alert { margin-top: 12px; display: flex; align-items: center; gap: 8px; padding: 10px 14px; background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; color: #166534; font-size: 13px; font-weight: 500; }
                .drawer-footer-actions { display: flex; justify-content: space-between; align-items: center; width: 100%; }
                .footer-btn-back { border: 1px solid #d1d5db; background-color: #ffffff; color: #374151; font-weight: 600; padding: 8px 16px; border-radius: 4px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-size: 14px; }
                .footer-btn-save { background-color: #2563eb; color: #ffffff; border: none; font-weight: 600; padding: 8px 20px; border-radius: 4px; cursor: pointer; font-size: 14px; }
                .vehicle-settings-actions { display: flex; justify-content: flex-end; margin-top: 12px; }
                .vehicle-settings-update-btn { background-color: #16a34a; color: #ffffff; border: none; height: 38px; border-radius: 4px; font-weight: 600; font-size: 13px; padding: 0 18px; cursor: pointer; }
                .vehicle-settings-update-btn:hover:not(:disabled) { background-color: #15803d; }
                .formula-builder-drawer button:disabled,
                .formula-builder-drawer input:disabled {
                  opacity: 0.55;
                  cursor: not-allowed !important;
                }
                .composer-token-pill.is-disabled {
                  opacity: 0.65;
                  cursor: not-allowed;
                }
                .composer-token-pill.is-disabled:hover {
                  text-decoration: none;
                }
              `}</style>

              {/* Section 1: Components & Charges */}
              <div className="formula-drawer-card">
                <div className="formula-drawer-card-header">
                  <div className="formula-drawer-step-badge">1</div>
                  <div className="formula-drawer-card-title-group">
                    <h5>Components & Charges</h5>
                    <p>Select Vehicle Type and define the pricing components</p>
                  </div>
                </div>

                <div className="mb-3">
                  <Select
                    label={renderFieldLabel("Vehicle Type")}
                    placeholder="Select Vehicle Type"
                    options={vehicleTypeDropdownOptions}
                    value={pickOption(vehicleTypeDropdownOptions, drawerCharge.vehicleTypeId)}
                    onChange={(opt) => {
                      setDrawerCharge((prev) => ({
                        ...prev,
                        vehicleTypeId: opt?.value || "",
                        vehicleType: opt?.label || "",
                      }));
                      if (drawerErrors.vehicleTypeId) {
                        setDrawerErrors((prev) => ({ ...prev, vehicleTypeId: "" }));
                      }
                    }}
                    isSearch
                    isDisabled={drawerControlsDisabled}
                    error={drawerTouched ? drawerErrors.vehicleTypeId : ""}
                    touched={drawerTouched}
                  />
                  {drawerTouched && drawerErrors.vehicleTypeId && (
                    <div className="error-message" style={{ color: "#ff0000de", fontSize: "12px", marginTop: "4px", marginLeft: "4px" }}>
                      {drawerErrors.vehicleTypeId}
                    </div>
                  )}
                </div>

                <div className="charges-builder-group">
                  <div className="component-composer-box">
                    <div className="sub-group-title">Trip Fixed Charges</div>
                    <div className="formula-drawer-grid-row" style={{ marginBottom: 0 }}>
                      <div className="formula-drawer-grid-col" style={{ flex: 1.5 }}>
                        <Select
                          label={renderFieldLabel("Charges Type", false)}
                          placeholder="Select Type"
                          options={componentTypeOptions}
                          value={pickOption(componentTypeOptions, tempChargeType)}
                          onChange={(option) => setTempChargeType(option?.value || "")}
                          isSearch={false}
                          isDisabled={drawerControlsDisabled}
                          required={false}
                        />
                      </div>
                      <div className="formula-drawer-grid-col">
                        <Input
                          label={renderFieldLabel("Value", false)}
                          type="number"
                          placeholder="0.00"
                          value={tempChargeValue}
                          onChange={(e) => setTempChargeValue(e.target.value)}
                          disabled={drawerControlsDisabled}
                          required={false}
                        />
                      </div>
                      <div className="formula-drawer-grid-col-btn" style={{ marginBottom: 0 }}>
                        <button
                          type="button"
                          className="add-charge-btn"
                          disabled={drawerControlsDisabled || !tempChargeType || !tempChargeValue}
                          onClick={handleAddChargeClick}
                        >
                          <PiPlus /> Add Charge
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="vehicle-settings-box">
                    <div className="sub-group-title">Vehicle Settings</div>
                    <div className="formula-drawer-grid-row" style={{ marginBottom: 0 }}>
                      <div className="formula-drawer-grid-col">
                        <Input
                          label={renderFieldLabel("No. of Vehicles")}
                          type="number"
                          placeholder="e.g. 5"
                          value={drawerCharge.numberOfVehicle}
                          onChange={(e) => {
                            setDrawerCharge((prev) => ({ ...prev, numberOfVehicle: e.target.value }));
                            if (drawerErrors.numberOfVehicle) {
                              setDrawerErrors((prev) => ({ ...prev, numberOfVehicle: "" }));
                            }
                          }}
                          disabled={vehicleSettingsDisabled}
                          error={drawerTouched ? drawerErrors.numberOfVehicle : ""}
                          touched={drawerTouched}
                        />

                      </div>
                      <div className="formula-drawer-grid-col">
                        <Input
                          label={renderFieldLabel("Monthly Rent", false)}
                          type="number"
                          placeholder="0.00"
                          value={drawerCharge.monthlyCharges}
                          onChange={(e) =>
                            setDrawerCharge((prev) => ({ ...prev, monthlyCharges: e.target.value }))
                          }
                          disabled={vehicleSettingsDisabled}
                          error={drawerTouched ? drawerErrors.monthlyCharges : ""}
                          touched={drawerTouched}
                          required={false}
                        />
                      </div>
                    </div>
                    {isExistingVehicleType && (
                      <div className="vehicle-settings-actions">
                        <button
                          type="button"
                          className="vehicle-settings-update-btn"
                          onClick={handleUpdateVehicleSettings}
                          disabled={vehicleSettingsUpdateDisabled}
                        >
                          {updatingVehicleSetting ? "Updating..." : "Update"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {(() => {
                  const activeComponents = [];
                  if (drawerCharge.variableType) {
                    activeComponents.push({
                      type: "variable",
                      label: getVariableTypeLabel(drawerCharge.variableType),
                      value: drawerCharge.variableRate,
                      variableType: drawerCharge.variableType,
                    });
                  }
                  (drawerCharge.fixedCharges || []).forEach((fc, fcIdx) => {
                    activeComponents.push({
                      type: "fixed",
                      label: getFixedTypeLabel(fc.fixedType),
                      value: fc.fixedCharge,
                      id: fc.id,
                      fixedType: fc.fixedType,
                    });
                  });

                  if (activeComponents.length > 0) {
                    return (
                      <div className="added-components-card">
                        <div className="active-components-container" style={{ marginTop: 0 }}>
                          {activeComponents.map((item, itemIdx) => {
                            const isUsed =
                              item.type === "variable"
                                ? (drawerCharge.formulaTokens || []).some((t) => t.type === "variableRate")
                                : (drawerCharge.formulaTokens || []).some(
                                  (t) => t.type === "fixedCharge" && t.metadata?.fixedType === item.fixedType
                                );

                            return (
                              <div key={item.id || `active-${itemIdx}`} className={`pricing-component-card is-${item.type}`}>
                                <div className="card-top-row">
                                  <span className="card-badge-name">{item.label.toUpperCase()}</span>
                                  <button
                                    type="button"
                                    className="card-remove-btn"
                                    disabled={drawerControlsDisabled}
                                    onClick={() => {
                                      if (drawerControlsDisabled) return;
                                      if (item.type === "variable") {
                                        handleRemoveVariableCharge();
                                      } else {
                                        handleRemoveFixedChargeItem(item.id, item.fixedType);
                                      }
                                    }}
                                  >
                                    <PiXBold style={{ fontSize: "10px" }} />
                                  </button>
                                </div>
                                <div className="card-middle-row">Rs {item.value}</div>
                                <div className="card-bottom-row">{isUsed ? "Used in formula" : "Not used in formula"}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="empty-placeholder" style={{ marginTop: "12px", width: "100%" }}>
                      No components yet. Add a charge above (e.g. Per KM = 150).
                    </div>
                  );
                })()}
              </div>

              {/* Section 2: Formula Composer */}
              <div className="formula-drawer-card">
                <div className="formula-drawer-card-header">
                  <div className="formula-drawer-step-badge">2</div>
                  <div className="formula-drawer-card-title-group">
                    <h5>Formula Composer</h5>
                    <p>Click a category, then a token, to build the formula. Click a token to remove it.</p>
                  </div>
                </div>

                <div className="category-tab-bar">
                  {/* Trip Data */}
                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeCategory === "tripData" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveCategory(activeCategory === "tripData" ? null : "tripData")}
                    >
                      <span className="category-dot dot-trip-data"></span>
                      Trip Data
                    </button>
                    {activeCategory === "tripData" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown">
                        <span className="popover-heading">SELECT TRIP DATA</span>
                        <div className="popover-grid">
                          {tripDataTypeOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className="popover-grid-item"
                              disabled={drawerControlsDisabled}
                              onClick={() => {
                                appendFormulaToken(createFormulaToken("tripData", opt.value, opt.label));
                                setActiveCategory(null);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Charges */}
                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeCategory === "charges" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveCategory(activeCategory === "charges" ? null : "charges")}
                    >
                      <span className="category-dot dot-charges"></span>
                      Charges
                    </button>
                    {activeCategory === "charges" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown">
                        <span className="popover-heading">SELECT A CHARGE</span>
                        {(() => {
                          const hasVar = !!drawerCharge.variableType;
                          const hasFixed = drawerCharge.fixedCharges && drawerCharge.fixedCharges.length > 0;
                          if (!hasVar && !hasFixed) {
                            return <div style={{ fontSize: "12px", color: "#9ca3af" }}>No charges configured in Section 1.</div>;
                          }
                          return (
                            <div className="popover-grid">
                              {hasVar && (
                                <button
                                  type="button"
                                  className="popover-grid-item"
                                  disabled={drawerControlsDisabled}
                                  onClick={() => {
                                    appendFormulaToken(
                                      createFormulaToken(
                                        "variableRate",
                                        drawerCharge.variableRate,
                                        `${getVariableTypeLabel(drawerCharge.variableType)} (${drawerCharge.variableRate})`
                                      )
                                    );
                                    setActiveCategory(null);
                                  }}
                                >
                                  {getVariableTypeLabel(drawerCharge.variableType)} ({drawerCharge.variableRate})
                                </button>
                              )}
                              {hasFixed &&
                                drawerCharge.fixedCharges.map((fc, fcIdx) => (
                                  <button
                                    key={fc.id || fcIdx}
                                    type="button"
                                    className="popover-grid-item"
                                    disabled={drawerControlsDisabled}
                                    onClick={() => {
                                      appendFormulaToken(
                                        createFormulaToken(
                                          "fixedCharge",
                                          fc.fixedCharge,
                                          `${getFixedTypeLabel(fc.fixedType)} (${fc.fixedCharge})`,
                                          { fixedType: fc.fixedType }
                                        )
                                      );
                                      setActiveCategory(null);
                                    }}
                                  >
                                    {getFixedTypeLabel(fc.fixedType)} ({fc.fixedCharge})
                                  </button>
                                ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Constants */}
                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeCategory === "constants" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveCategory(activeCategory === "constants" ? null : "constants")}
                    >
                      <span className="category-dot dot-constants"></span>
                      Constants
                    </button>
                    {activeCategory === "constants" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown">
                        <span className="popover-heading">ENTER A CONSTANT</span>
                        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                          <input
                            type="number"
                            className="popover-text-input"
                            placeholder="e.g. 15"
                            value={drawerCharge.formulaHardcodedValue || ""}
                            disabled={drawerControlsDisabled}
                            onChange={(e) =>
                              setDrawerCharge((prev) => ({ ...prev, formulaHardcodedValue: e.target.value }))
                            }
                          />
                          <button
                            type="button"
                            className="popover-add-btn"
                            onClick={() => {
                              handleAddHardcodedValueToFormula();
                              setActiveCategory(null);
                            }}
                            disabled={drawerControlsDisabled || !String(drawerCharge.formulaHardcodedValue || "").trim()}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Operators */}
                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeCategory === "operators" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveCategory(activeCategory === "operators" ? null : "operators")}
                    >
                      <span className="category-dot dot-operators"></span>
                      Operators
                    </button>
                    {activeCategory === "operators" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown" style={{ minWidth: "240px" }}>
                        <span className="popover-heading">OPERATORS & BRACKETS</span>
                        <div className="popover-operators-grid">
                          {["+", "-", "*", "/", "%", "(", ")"].map((operator) => (
                            <button
                              key={operator}
                              type="button"
                              className="popover-operator-btn"
                              disabled={drawerControlsDisabled}
                              onClick={() => {
                                handleAddOperatorToFormula(operator);
                                setActiveCategory(null);
                              }}
                            >
                              {operator === "/" ? "÷" : operator === "*" ? "×" : operator}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="formula-composer-workspace">
                  <div className="composer-tokens-list">
                    {!drawerCharge.formulaTokens || drawerCharge.formulaTokens.length === 0 ? (
                      <div style={{ color: "#9ca3af", fontSize: "13px", padding: "8px 0" }}>
                        Start building: add Trip Data, Charges, Constants or Operators above.
                      </div>
                    ) : (
                      drawerCharge.formulaTokens.map((token, tokenIdx) => (
                        <span
                          key={token.id || `composer-${tokenIdx}`}
                          className={`composer-token-pill type-${token.type} ${drawerControlsDisabled ? "is-disabled" : ""}`}
                          onClick={() => {
                            if (!drawerControlsDisabled) {
                              handleRemoveFormulaToken(tokenIdx);
                            }
                          }}
                          title={drawerControlsDisabled ? "" : "Click to remove"}
                        >
                          {resolveFormulaTokenLabel(token)}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="composer-workspace-footer">
                    <button
                      type="button"
                      className="action-btn-outline"
                      onClick={handleBackspaceFormula}
                      disabled={drawerControlsDisabled || !drawerCharge.formulaTokens || drawerCharge.formulaTokens.length === 0}
                    >
                      Backspace
                    </button>
                    <button
                      type="button"
                      className="action-btn-outline"
                      style={{ color: "#ef4444", borderColor: "#fecaca" }}
                      onClick={handleClearFormula}
                      disabled={drawerControlsDisabled || !drawerCharge.formulaTokens || drawerCharge.formulaTokens.length === 0}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="live-preview-section">
                  <div className="live-preview-label">Live Preview</div>
                  <div className="live-preview-box">
                    Revenue = {buildFormulaDisplay(drawerCharge.formulaTokens || []) || "..."}
                  </div>

                  {(() => {
                    const formulaErr = validateFormulaTokens(drawerCharge.formulaTokens || []);
                    if (formulaErr) {
                      return (
                        <div className="formula-error-alert">
                          <PiWarningCircle style={{ fontSize: "16px" }} />
                          {formulaErr}
                        </div>
                      );
                    }
                    if (drawerCharge.formulaTokens && drawerCharge.formulaTokens.length > 0) {
                      return (
                        <div className="formula-success-alert">
                          <PiCheckCircle style={{ fontSize: "16px" }} />
                          Formula is valid
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Section 3: Shortages & Deductions */}
              <div className="formula-drawer-card">
                <div className="formula-drawer-card-header">
                  <div className="formula-drawer-step-badge">3</div>
                  <div className="formula-drawer-card-title-group">
                    <h5>Shortages & Deductions</h5>
                    <p>Define shortage charges deducted from calculated revenue</p>
                  </div>
                </div>

                <div className="category-tab-bar">
                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeShortagesCategory === "tripData" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveShortagesCategory(activeShortagesCategory === "tripData" ? null : "tripData")}
                    >
                      <span className="category-dot dot-trip-data"></span>
                      Trip Data
                    </button>
                    {activeShortagesCategory === "tripData" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown">
                        <span className="popover-heading">SELECT TRIP DATA</span>
                        <div className="popover-grid">
                          {storageTripDataTypeOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className="popover-grid-item"
                              disabled={drawerControlsDisabled}
                              onClick={() => {
                                appendShortagesFormulaToken(createFormulaToken("tripData", opt.value, opt.label));
                                setActiveShortagesCategory(null);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="tab-popover-wrapper">
                    <button
                      type="button"
                      className={`category-tab-btn ${activeShortagesCategory === "operators" ? "is-active" : ""}`}
                      disabled={drawerControlsDisabled}
                      onClick={() => setActiveShortagesCategory(activeShortagesCategory === "operators" ? null : "operators")}
                    >
                      <span className="category-dot dot-operators"></span>
                      Operators
                    </button>
                    {activeShortagesCategory === "operators" && !drawerControlsDisabled && (
                      <div className="composer-popover-dropdown" style={{ minWidth: "240px" }}>
                        <span className="popover-heading">OPERATORS & BRACKETS</span>
                        <div className="popover-operators-grid">
                          {["+", "-", "*", "/", "%", "(", ")"].map((operator) => (
                            <button
                              key={operator}
                              type="button"
                              className="popover-operator-btn"
                              disabled={drawerControlsDisabled}
                              onClick={() => {
                                handleAddOperatorToShortagesFormula(operator);
                                setActiveShortagesCategory(null);
                              }}
                            >
                              {operator === "/" ? "÷" : operator === "*" ? "×" : operator}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="formula-composer-workspace">
                  <div className="composer-tokens-list">
                    {!drawerCharge.shortagesFormulaTokens || drawerCharge.shortagesFormulaTokens.length === 0 ? (
                      <div style={{ color: "#9ca3af", fontSize: "13px", padding: "8px 0" }}>
                        Start building: add Trip Data or Operators above.
                      </div>
                    ) : (
                      drawerCharge.shortagesFormulaTokens.map((token, tokenIdx) => (
                        <span
                          key={token.id || `composer-shortages-${tokenIdx}`}
                          className={`composer-token-pill type-${token.type} ${drawerControlsDisabled ? "is-disabled" : ""}`}
                          onClick={() => {
                            if (!drawerControlsDisabled) {
                              handleRemoveShortagesFormulaToken(tokenIdx);
                            }
                          }}
                          title={drawerControlsDisabled ? "" : "Click to remove"}
                        >
                          {resolveFormulaTokenLabel(token)}
                        </span>
                      ))
                    )}
                  </div>

                  <div className="composer-workspace-footer">
                    <button
                      type="button"
                      className="action-btn-outline"
                      onClick={handleBackspaceShortagesFormula}
                      disabled={drawerControlsDisabled || !drawerCharge.shortagesFormulaTokens || drawerCharge.shortagesFormulaTokens.length === 0}
                    >
                      Backspace
                    </button>
                    <button
                      type="button"
                      className="action-btn-outline"
                      style={{ color: "#ef4444", borderColor: "#fecaca" }}
                      onClick={handleClearShortagesFormula}
                      disabled={drawerControlsDisabled || !drawerCharge.shortagesFormulaTokens || drawerCharge.shortagesFormulaTokens.length === 0}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="live-preview-section">
                  <div className="live-preview-label">Live Preview</div>
                  <div className="live-preview-box">
                    Deduction = {buildFormulaDisplay(drawerCharge.shortagesFormulaTokens || []) || "..."}
                  </div>

                  {(() => {
                    const shortagesFormulaErr =
                      drawerCharge.shortagesFormulaTokens && drawerCharge.shortagesFormulaTokens.length > 0
                        ? validateFormulaTokens(drawerCharge.shortagesFormulaTokens)
                        : null;
                    if (shortagesFormulaErr) {
                      return (
                        <div className="formula-error-alert">
                          <PiWarningCircle style={{ fontSize: "16px" }} />
                          {shortagesFormulaErr}
                        </div>
                      );
                    }
                    if (drawerCharge.shortagesFormulaTokens && drawerCharge.shortagesFormulaTokens.length > 0) {
                      return (
                        <div className="formula-success-alert">
                          <PiCheckCircle style={{ fontSize: "16px" }} />
                          Formula is valid
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </Drawer>

            {/* Assign Vehicles Drawer */}
            <Drawer
              placement="right"
              onClose={() => setIsAssignModalOpen(false)}
              open={isAssignModalOpen}
              width={650}
              closable={false}
              className="assign-vehicles-drawer"
              title={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: "18px", color: "#0f172a" }}>
                      Assign Vehicles to Contract
                    </h4>
                    <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                      Select vehicle type and check vehicles to assign them to this contract.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsAssignModalOpen(false)}
                    style={{
                      border: "none",
                      background: "#f1f5f9",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#475569",
                      padding: 0,
                    }}
                  >
                    <PiXBold style={{ fontSize: "15px" }} />
                  </button>
                </div>
              }
              footer={
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                    Selected: <strong style={{ color: "#4f46e5" }}>{assignSelectedVehicleIds.length}</strong> vehicle(s)
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setIsAssignModalOpen(false)}
                      disabled={savingAssignedVehicles}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{
                        backgroundColor: "#4f46e5",
                        borderColor: "#4f46e5",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      onClick={handleSaveAssignedVehicles}
                      disabled={savingAssignedVehicles || !assignVehicleTypeId}
                    >
                      {savingAssignedVehicles ? "Saving..." : "Save Assigned Vehicles"}
                    </button>
                  </div>
                </div>
              }
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {/* Vehicle Type Selector */}
                <div>
                  <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Select Vehicle Type <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    className="form-select"
                    value={assignVehicleTypeId}
                    onChange={(e) => handleVehicleTypeChangeInAssignModal(e.target.value)}
                    disabled
                    style={{
                      height: "42px",
                      fontSize: "14px",
                      borderRadius: "6px",
                      border: "1px solid #e2e8f0",
                      backgroundColor: "#f8fafc",
                      color: "#334155",
                      cursor: "not-allowed",
                      fontWeight: 500,
                    }}
                  >
                    <option value="" disabled>-- Select Vehicle Type --</option>
                    {configuredVehicleTypes.map((cvt) => {
                      const vtId = cvt.vehicle_type_id || cvt.vehicle_type?.id;
                      const vtTitle = cvt.vehicle_type?.title || cvt.vehicle_type?.name || `Vehicle Type ${vtId}`;
                      return (
                        <option key={vtId} value={String(vtId)}>
                          {vtTitle} (Quota: {cvt.no_of_vehicle || 0} vehicles)
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Search available vehicles */}
                {assignVehicleTypeId && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "13px", fontWeight: 600, color: "#334155", margin: 0 }}>
                        Available Fleet Vehicles
                      </label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          type="button"
                          onClick={() => setAssignSelectedVehicleIds(availableVehicles.map((v) => v.id))}
                          style={{ border: "none", background: "none", color: "#4f46e5", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                        >
                          Select All
                        </button>
                        <span style={{ color: "#cbd5e1" }}>|</span>
                        <button
                          type="button"
                          onClick={() => setAssignSelectedVehicleIds([])}
                          style={{ border: "none", background: "none", color: "#64748b", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    <div style={{ position: "relative", marginBottom: "12px" }}>
                      <PiMagnifyingGlass
                        style={{
                          position: "absolute",
                          left: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#94a3b8",
                          fontSize: "15px",
                        }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by registration number..."
                        value={assignSearchQuery}
                        onChange={(e) => setAssignSearchQuery(e.target.value)}
                        style={{
                          paddingLeft: "36px",
                          height: "38px",
                          fontSize: "13px",
                          borderRadius: "6px",
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    </div>

                    {loadingAvailableVehicles ? (
                      <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
                        <SpinnerLoader />
                        <div style={{ marginTop: "10px", fontSize: "13px" }}>Loading fleet vehicles...</div>
                      </div>
                    ) : (
                      <div
                        style={{
                          maxHeight: "380px",
                          overflowY: "auto",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          backgroundColor: "#ffffff",
                          padding: "8px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {availableVehicles
                          .filter((v) => {
                            const q = assignSearchQuery.trim().toLowerCase();
                            if (!q) return true;
                            return (
                              (v.registration_number || "").toLowerCase().includes(q) ||
                              (v.registration_number_slug || "").toLowerCase().includes(q) ||
                              String(v.id || "").includes(q)
                            );
                          })
                          .map((veh) => {
                            const isChecked = assignSelectedVehicleIds.includes(veh.id);
                            return (
                              <div
                                key={veh.id}
                                onClick={() => handleToggleVehicleSelection(veh.id)}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "10px 14px",
                                  borderRadius: "6px",
                                  backgroundColor: isChecked ? "#eff6ff" : "#f8fafc",
                                  border: `1px solid ${isChecked ? "#93c5fd" : "#e2e8f0"}`,
                                  cursor: "pointer",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => {}}
                                    style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                  />
                                  <div
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      padding: "2px 8px",
                                      borderRadius: "4px",
                                      border: "1.5px solid #1e293b",
                                      backgroundColor: "#ffffff",
                                      fontSize: "12px",
                                      fontWeight: 800,
                                      fontFamily: "monospace",
                                      color: "#0f172a",
                                    }}
                                  >
                                    {veh.registration_number?.toUpperCase() || `#${veh.id}`}
                                  </div>
                                </div>
                                {isChecked && (
                                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#2563eb", display: "flex", alignItems: "center", gap: "4px" }}>
                                    <PiCheck /> Selected
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        {availableVehicles.length === 0 && (
                          <div style={{ padding: "30px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                            No vehicles available for this vehicle type.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Drawer>
          </div>
        );
      }}
    </Formik>
  );
};

export default ContractDetails;
